import { DataService } from '../services/data.js';
import { formatMoney, formatHours, getIcon, showToast, getAlerceLogo } from '../components/ui.js';
import { PdfService } from '../services/pdfGenerator.js';
import { TimerService } from '../services/timer.js';

let selectedProjectId = null;
let timerUnsubscribe = null;
let subtaskLoggedFromTimer = false;
let cachedProjects = [];
let cachedRates = [];
let cachedClients = [];
let projectSearchTerm = '';
let projectStatusFilter = 'all';
let wbsSearchTerm = '';
let collapsedTaskIds = new Set();
let subtaskSortOrder = (typeof localStorage !== 'undefined' && localStorage.getItem('alerce_subtask_sort_order')) || 'desc';
let projectFilterDateFrom = '';
let projectFilterDateTo = '';

// Helper: verificar si una fecha ISO YYYY-MM-DD cae dentro del rango [from, to]
export function isDateInRange(dateStr, from, to) {
  if (!dateStr) return false;
  const d = String(dateStr).trim().substring(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

// Helper: calcular rango para presets de fecha de proyecto
export function getProjectDatePresetRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (preset === 'this-month') {
    const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
  } else if (preset === 'last-month') {
    const prevY = m === 0 ? y - 1 : y;
    const prevM = m === 0 ? 11 : m - 1;
    const from = `${prevY}-${String(prevM + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(prevY, prevM + 1, 0).getDate();
    const to = `${prevY}-${String(prevM + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return { from, to };
  } else if (preset === 'this-year') {
    return { from: `${y}-01-01`, to: `${y}-12-31` };
  }
  return { from: '', to: '' };
}

// Helper: calcular métricas del proyecto en base al filtro de fechas
export function calculateProjectPeriodMetrics(project, dateFrom, dateTo) {
  if (!project) return { periodActualHours: 0, periodDevengado: 0, hasFilter: false };
  const hasFilter = Boolean(dateFrom || dateTo);
  const now = new Date();
  const currentY = now.getFullYear();
  const currentM = now.getMonth();

  // 1. Horas reales en el período
  const allSubtasks = (project.tasks || []).flatMap(t => t.subtasks || []);
  const periodSubtasks = allSubtasks.filter(st => {
    if (!hasFilter) return true;
    const stDate = st.date || '';
    return isDateInRange(stDate, dateFrom, dateTo);
  });

  const periodActualHours = periodSubtasks.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);

  // 2. Devengado en el período
  let periodDevengado = 0;
  const isInternal = project.client_id === 12 || (project.name || '').toLowerCase().includes('alerce');

  if (!isInternal) {
    if (project.revenue_type === 'recurrente') {
      const budgetAmount = Number(project.budget_amount || 0);
      if (project.start_date) {
        const parts = project.start_date.split('-');
        const startY = parseInt(parts[0], 10);
        const startM = parseInt(parts[1], 10) - 1;
        const endParts = project.end_date ? project.end_date.split('-') : null;
        const endY = endParts ? parseInt(endParts[0], 10) : currentY;
        const endM = endParts ? parseInt(endParts[1], 10) - 1 : currentM;

        if (hasFilter) {
          let monthsCount = 0;
          for (let y = startY; y <= Math.min(endY, currentY); y++) {
            const mStart = (y === startY) ? startM : 0;
            const mEnd = (y === endY || y === currentY) ? Math.min((y === endY ? endM : 11), (y === currentY ? currentM : 11)) : 11;
            for (let m = mStart; m <= mEnd; m++) {
              const mStr = `${y}-${String(m + 1).padStart(2, '0')}-01`;
              if ((!dateFrom || mStr >= dateFrom.substring(0, 7) + '-01') && 
                  (!dateTo || mStr <= dateTo.substring(0, 7) + '-28')) {
                monthsCount++;
              }
            }
          }
          periodDevengado = monthsCount * budgetAmount;
        } else {
          const targetEndY = Math.min(endY, currentY);
          const targetEndM = endY < currentY ? endM : Math.min(endM, currentM);
          const monthsCount = Math.max(0, (targetEndY - startY) * 12 + (targetEndM - startM) + 1);
          periodDevengado = monthsCount * budgetAmount;
        }
      } else {
        periodDevengado = budgetAmount;
      }
    } else if (project.billing_mode === 'time_and_materials') {
      const baseRate = Number(project.rate?.hourly_rate || 0);
      const disc = Number(project.discount_percent || 0);
      const effRate = disc > 0 ? baseRate * (1 - disc / 100) : baseRate;

      periodDevengado = periodSubtasks.reduce((sum, st) => {
        const stRate = (st.hourly_rate_applied !== null && st.hourly_rate_applied !== undefined)
          ? Number(st.hourly_rate_applied)
          : effRate;
        return sum + (Number(st.actual_hours || 0) * stRate);
      }, 0);
    } else {
      // Presupuesto Fijo
      const budgetAmount = Number(project.budget_amount || 0);
      if (hasFilter) {
        const effectiveProjectDate = project.end_date || project.start_date;
        if (effectiveProjectDate && isDateInRange(effectiveProjectDate, dateFrom, dateTo)) {
          periodDevengado = budgetAmount;
        } else {
          periodDevengado = 0;
        }
      } else {
        periodDevengado = budgetAmount;
      }
    }
  }

  return {
    periodActualHours,
    periodDevengado: Math.round(periodDevengado),
    hasFilter
  };
}

// Helper: ordenar subtareas por fecha (descendente o ascendente) con desempate por id
export function sortSubtasksByDate(subtasks, order = 'desc') {
  if (!Array.isArray(subtasks)) return [];
  return [...subtasks].sort((a, b) => {
    const valA = a?.date ? String(a.date).trim() : '';
    const valB = b?.date ? String(b.date).trim() : '';
    if (!valA && valB) return 1;
    if (valA && !valB) return -1;
    if (valA !== valB) {
      return order === 'desc'
        ? valB.localeCompare(valA)
        : valA.localeCompare(valB);
    }
    const idA = Number(a?.id) || 0;
    const idB = Number(b?.id) || 0;
    return order === 'desc' ? idB - idA : idA - idB;
  });
}

// Helper: badges de estado de proyecto con colores semánticos
function getProjectStatusBadge(status) {
  let colorClasses = 'bg-surface-subtle text-text-secondary border-border';
  switch (status) {
    case 'En curso':
      colorClasses = 'bg-blue-50 text-blue-700 border-blue-200';
      break;
    case 'Completado':
      colorClasses = 'bg-emerald-50 text-emerald-700 border-emerald-200';
      break;
    case 'En pausa':
      colorClasses = 'bg-amber-50 text-amber-700 border-amber-200';
      break;
    case 'No iniciado':
      colorClasses = 'bg-zinc-100 text-zinc-600 border-zinc-200';
      break;
    case 'Cancelado':
      colorClasses = 'bg-rose-50 text-rose-700 border-rose-200';
      break;
  }
  return `<span class="shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded border ${colorClasses}">${status || 'Sin estado'}</span>`;
}

// Helper: filtrado de proyectos en memoria por término y estado
function getFilteredProjects() {
  const pTerm = projectSearchTerm.toLowerCase().trim();
  return cachedProjects.filter(p => {
    if (projectStatusFilter && projectStatusFilter !== 'all') {
      if (p.status !== projectStatusFilter) return false;
    }
    if (!pTerm) return true;
    return (p.name || '').toLowerCase().includes(pTerm) ||
      (p.client?.name || '').toLowerCase().includes(pTerm) ||
      (p.description || '').toLowerCase().includes(pTerm) ||
      (p.status || '').toLowerCase().includes(pTerm);
  });
}

// Helper: render de una tarjeta de proyecto individual
function renderProjectCardHtml(p, isSelected) {
  const isTimeAndMaterials = p.billing_mode === 'time_and_materials';
  return `
    <div 
      class="project-card p-4 rounded-md border transition-all cursor-pointer ${
        isSelected 
          ? 'bg-surface border-primary shadow-card ring-1 ring-primary' 
          : 'bg-surface border-border hover:border-zinc-400'
      }"
      data-project-id="${p.id}"
    >
      <div class="flex items-start justify-between gap-2">
        <h3 class="font-bold text-sm text-text-primary leading-tight">${p.name}</h3>
        ${getProjectStatusBadge(p.status)}
      </div>

      <p class="text-xs text-text-secondary mt-1 font-medium">${p.client?.name || 'Cliente sin asignar'}</p>

      <!-- Billing Badge & Financial Amount (Highlighted) -->
      <div class="flex items-center justify-between gap-2 mt-2">
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="px-1.5 py-0.5 rounded font-medium text-[10px] ${
            isTimeAndMaterials 
              ? 'bg-blue-50 text-blue-700 border border-blue-200' 
              : 'bg-zinc-100 text-zinc-700 border border-zinc-200'
          }">
            ${isTimeAndMaterials ? 'Por Horas Reales' : 'Presupuesto Fijo'}
          </span>
          <span class="px-1.5 py-0.5 rounded font-mono font-medium text-[10px] ${
            p.revenue_type === 'recurrente'
              ? 'bg-purple-50 text-purple-700 border border-purple-200'
              : 'bg-surface-subtle text-text-secondary border border-border'
          }">
            ${p.revenue_type === 'recurrente' ? 'Recurrente' : 'Puntual'}
          </span>
        </div>
        <span class="font-mono font-bold text-xs ${p.revenue_type === 'recurrente' ? 'text-purple-700' : isTimeAndMaterials ? 'text-emerald-600' : 'text-text-primary'}">
          ${p.revenue_type === 'recurrente'
            ? `${formatMoney(p.budget_amount, p.currency)} /mes`
            : isTimeAndMaterials 
              ? `${formatMoney(p.billable_amount, p.rate?.currency)}`
              : `${formatMoney(p.budget_amount, p.currency)}`}
        </span>
      </div>

      <!-- Devengado Mes Actual -->
      <div class="flex items-center justify-between text-[10px] font-mono text-text-secondary mt-1">
        <span>Mes actual:</span>
        <b class="text-text-primary font-semibold">${formatMoney(p.current_month_devengado || 0, p.rate?.currency || p.currency)}</b>
      </div>

      <!-- Financial and Progress Metrics -->
      ${isTimeAndMaterials ? `
        <div class="mt-2.5 pt-2 border-t border-border/60 flex items-center justify-between text-[11px] font-mono">
          <span class="text-text-secondary">Reales: <b class="text-text-primary">${formatHours(p.total_actual_hours)}</b></span>
          <span class="text-text-secondary">${p.rate?.name || 'Local'} (${formatMoney(p.rate?.hourly_rate, p.rate?.currency)}/h)${p.discount_percent > 0 ? ` <b class="text-emerald-700">-${p.discount_percent}%</b>` : ''}</span>
        </div>
      ` : `
        <div class="mt-2.5 pt-2 border-t border-border/60 space-y-1">
          <div class="flex items-center justify-between text-[10px] font-mono">
            <span class="text-text-secondary">Consumo: <b class="${p.is_over_budget ? 'text-red-600' : p.budget_consumption_percent >= 75 ? 'text-amber-700' : 'text-text-primary'}">${p.budget_consumption_percent?.toFixed(0) || 0}%</b> (${formatHours(p.total_actual_hours)}/${p.budget_hours || 0}h)</span>
            ${p.discount_percent > 0 ? `<span class="text-[10px] font-mono text-emerald-700 bg-emerald-50 px-1 rounded border border-emerald-200">-${p.discount_percent}%</span>` : ''}
          </div>
          <div class="w-full bg-zinc-200 rounded-full h-1.5 overflow-hidden">
            <div class="h-full rounded-full transition-all ${
              p.is_over_budget ? 'bg-red-600' : (p.budget_consumption_percent >= 75 ? 'bg-amber-500' : 'bg-emerald-500')
            }" style="width: ${Math.min(100, p.budget_consumption_percent || 0)}%"></div>
          </div>
          ${p.is_over_budget ? `
            <div class="flex items-center justify-between text-[10px] font-bold text-red-600">
              <span>¡Trabajando gratis!</span>
              <span>+${formatHours(p.over_budget_hours)}</span>
            </div>
          ` : ''}
        </div>
      `}
    </div>
  `;
}

// Helper: render de una tarea con su cabecera cliqueable y subtareas
function renderTaskCardHtml(task, isCollapsed, wTerm) {
  const visibleSubtasks = task.visibleSubtasks !== undefined ? task.visibleSubtasks : (task.subtasks || []);
  const taskActualHours = visibleSubtasks.reduce((acc, st) => acc + Number(st.actual_hours || 0), 0);
  const isDone = task.status === 'Completada';
  const sortedSubtasks = sortSubtasksByDate(visibleSubtasks, subtaskSortOrder);
  const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
  const pCurrency = curProj?.rate?.currency || curProj?.currency || 'ARS';
  const totalSubtasksCount = (task.subtasks || []).length;
  const hasDateFilter = Boolean(projectFilterDateFrom || projectFilterDateTo);

  // Devengado de las subtareas visibles en el período
  const taskPeriodAccrued = visibleSubtasks.reduce((acc, st) => {
    return acc + Math.round(Number(st.actual_hours || 0) * Number(st.hourly_rate_applied || 0));
  }, 0);

  return `
    <div class="border border-border rounded-md overflow-hidden bg-white">
      <!-- Task Header Row (cliqueable para colapsar/desplegar) -->
      <div 
        class="task-header-row p-3.5 bg-surface-subtle flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-border/80 cursor-pointer hover:bg-zinc-100 transition-colors select-none"
        data-task-id="${task.id}"
        title="Haz clic para desplegar o plegar subtareas"
      >
        <div class="space-y-1 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <span 
              class="p-1 -ml-1 text-text-secondary hover:text-text-primary transition-transform rounded"
              id="task-chevron-${task.id}"
            >
              ${getIcon(isCollapsed ? 'chevron-right' : 'chevron-down', 'w-4 h-4')}
            </span>
            <span class="font-bold text-sm text-text-primary">${task.title}</span>
            <span class="text-[10px] font-mono px-2 py-0.5 rounded border ${
              isDone ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-surface text-text-secondary border-border'
            }">
              ${task.status}
            </span>
            <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-text-secondary">
              ${task.priority}
            </span>
            ${task.start_date ? `
              <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-text-secondary flex items-center gap-1" title="Fecha de inicio: ${task.start_date}${task.due_date ? ` • Vencimiento: ${task.due_date}` : ''}">
                ${getIcon('calendar', 'w-3 h-3 text-text-secondary')}
                <span>${task.start_date}${task.due_date ? ` → ${task.due_date}` : ''}</span>
              </span>
            ` : ''}
            <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-text-secondary font-semibold" title="Cantidad de subtareas visibles">
              ${visibleSubtasks.length}${hasDateFilter && visibleSubtasks.length !== totalSubtasksCount ? `/${totalSubtasksCount}` : ''} subtarea${visibleSubtasks.length === 1 ? '' : 's'}
            </span>
          </div>
          <p class="text-[11px] font-mono text-text-secondary ml-6">
            Estimadas: <b>${task.estimated_hours || 0}h</b> • Reales ${hasDateFilter ? 'en período' : 'acumuladas'}: <b class="text-text-primary">${formatHours(taskActualHours)}</b>
            ${curProj?.billing_mode === 'time_and_materials' ? ` • Devengado: <b class="text-emerald-700">${formatMoney(taskPeriodAccrued, pCurrency)}</b>` : (task.accrued_amount !== undefined ? ` • Devengado: <b class="text-emerald-700">${formatMoney(task.accrued_amount, pCurrency)}</b>` : '')}
          </p>
        </div>
        <div class="flex items-center gap-2 self-start shrink-0" onclick="event.stopPropagation()">
          <button 
            class="btn-edit-task px-2.5 py-1 text-xs font-semibold rounded bg-surface border border-border hover:bg-border/30 text-text-primary transition-colors flex items-center gap-1 shadow-sm"
            data-task-id="${task.id}"
            title="Editar tarea"
          >
            ${getIcon('edit', 'w-3 h-3')}
            <span>Editar</span>
          </button>
          <button 
            class="btn-log-subtask-quick px-2.5 py-1 text-xs font-semibold rounded bg-surface border border-border hover:bg-border/30 text-text-primary transition-colors flex items-center gap-1 shadow-sm"
            data-task-id="${task.id}"
            data-task-title="${task.title}"
          >
            ${getIcon('plus', 'w-3 h-3')}
            <span>Cargar horas</span>
          </button>
        </div>
      </div>

      <!-- Subtasks Nested List -->
      <div id="subtasks-container-${task.id}" class="divide-y divide-border/40 p-2 ${isCollapsed ? 'hidden' : ''}">
        ${visibleSubtasks.length === 0 ? `
          <p class="text-xs text-text-secondary italic py-2 px-3">
            ${wTerm || hasDateFilter ? 'No se encontraron subtareas que coincidan con los filtros aplicados.' : 'Sin subtareas imputadas aún.'}
          </p>
        ` : `
          ${visibleSubtasks.length > 1 ? `
            <div class="px-3 py-1.5 bg-surface-subtle/50 rounded flex items-center justify-between text-[11px] font-mono text-text-secondary mb-1 border-b border-border/30">
              <span class="font-medium">${visibleSubtasks.length} subtareas ${hasDateFilter ? 'en período' : 'registradas'}</span>
              <button 
                type="button" 
                class="btn-toggle-subtask-sort flex items-center gap-1.5 px-2 py-0.5 rounded border border-border bg-white hover:bg-surface text-text-primary text-[10px] font-semibold transition-colors shadow-2xs cursor-pointer"
                title="Alternar orden por fecha (actualmente: ${subtaskSortOrder === 'desc' ? 'más recientes primero' : 'más antiguas primero'})"
              >
                ${getIcon(subtaskSortOrder === 'desc' ? 'arrowDown' : 'arrowUp', 'w-3 h-3 text-primary')}
                <span>Fecha: <b class="text-primary">${subtaskSortOrder === 'desc' ? 'Más recientes primero' : 'Más antiguas primero'}</b></span>
              </button>
            </div>
          ` : ''}
          ${sortedSubtasks.map(st => `
            <div class="py-2 px-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
              <div class="space-y-0.5">
                <span class="text-text-primary">${st.description}</span>
                <p class="text-[10px] font-mono text-text-secondary">
                  <span class="font-semibold text-text-primary bg-surface-subtle px-1 py-0.5 rounded border border-border/50">${st.date || 'Sin fecha'}</span> • Autor: ${st.author}
                </p>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                ${st.accrued_amount !== undefined ? `
                  <span class="font-mono font-semibold text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded" title="Tarifa aplicada: ${formatMoney(st.hourly_rate_applied || 0, pCurrency)}/h">
                    ${formatMoney(st.accrued_amount, pCurrency)}
                  </span>
                ` : ''}
                <span class="font-mono font-bold text-xs bg-surface-subtle px-2 py-0.5 rounded border border-border text-text-primary">
                  ${formatHours(st.actual_hours)}
                </span>
                <button 
                  class="btn-edit-subtask text-text-secondary hover:text-text-primary transition-colors p-1 rounded hover:bg-surface-subtle"
                  data-subtask-id="${st.id}"
                  title="Editar subtarea"
                >
                  ${getIcon('edit', 'w-3.5 h-3.5')}
                </button>
                <button 
                  class="btn-delete-subtask text-text-secondary hover:text-red-600 transition-colors p-1 rounded hover:bg-surface-subtle"
                  data-subtask-id="${st.id}"
                  title="Eliminar subtarea"
                >
                  ${getIcon('trash', 'w-3.5 h-3.5')}
                </button>
              </div>
            </div>
          `).join('')}
        `}
      </div>
    </div>
  `;
}

// Helper: obtener tareas filtradas en memoria para el proyecto seleccionado
function getFilteredTasksForSelectedProject() {
  const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
  if (!curProj) return [];
  const wTerm = wbsSearchTerm.toLowerCase().trim();
  const hasDateFilter = Boolean(projectFilterDateFrom || projectFilterDateTo);
  const tasks = curProj.tasks || [];

  return tasks.map(task => {
    const allSubtasks = task.subtasks || [];

    // Filter subtasks by date range (using subtask date, fallback to task start date)
    const dateFilteredSubtasks = allSubtasks.filter(st => {
      if (!hasDateFilter) return true;
      const stDate = st.date || task.start_date || '';
      return isDateInRange(stDate, projectFilterDateFrom, projectFilterDateTo);
    });

    // Check if task itself matches date filter: has subtasks in period OR task start_date is in period
    const taskDateMatches = !hasDateFilter ||
      (task.start_date && isDateInRange(task.start_date, projectFilterDateFrom, projectFilterDateTo)) ||
      dateFilteredSubtasks.length > 0;

    let visibleSubtasks = dateFilteredSubtasks;
    let textMatches = true;

    if (wTerm) {
      const taskTextMatches = (task.title || '').toLowerCase().includes(wTerm) ||
        (task.status || '').toLowerCase().includes(wTerm) ||
        (task.priority || '').toLowerCase().includes(wTerm);

      const matchingSubtasks = visibleSubtasks.filter(st =>
        (st.description || '').toLowerCase().includes(wTerm) ||
        (st.author || '').toLowerCase().includes(wTerm) ||
        (st.date || '').toLowerCase().includes(wTerm)
      );

      if (taskTextMatches) {
        textMatches = true;
      } else if (matchingSubtasks.length > 0) {
        visibleSubtasks = matchingSubtasks;
        textMatches = true;
      } else {
        textMatches = false;
        visibleSubtasks = [];
      }
    }

    const matches = taskDateMatches && textMatches;
    return {
      ...task,
      visibleSubtasks,
      matches
    };
  }).filter(t => t.matches);
}

export async function renderProjectsView() {
  const [projects, rates, clients] = await Promise.all([
    DataService.getProjects(),
    DataService.getRates(),
    DataService.getClients()
  ]);

  cachedProjects = projects;
  cachedRates = rates;
  cachedClients = clients;

  // Filter projects by search term & stage
  const filteredProjects = getFilteredProjects();

  if (!selectedProjectId && filteredProjects.length > 0) {
    selectedProjectId = filteredProjects[0].id;
  }

  const selectedProject = projects.find(p => p.id === Number(selectedProjectId)) || filteredProjects[0] || null;
  const periodMetrics = calculateProjectPeriodMetrics(selectedProject, projectFilterDateFrom, projectFilterDateTo);

  return `
    <div class="space-y-6">
      <!-- Main Content of Projects View (Hidden when printing so only the document sheet prints) -->
      <div id="projects-main-content" class="space-y-6 no-print">
        <!-- Header & Quick Actions -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-3 border-b border-border">
        <div>
          <h1 class="text-2xl font-bold tracking-tight text-text-primary">Proyectos y Registro de Horas</h1>
          <p class="text-xs text-text-secondary mt-0.5">Gestión operativa, jerarquía WBS y carga numérica de subtareas</p>
        </div>
        <div class="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button id="btn-open-billing-help-modal" class="px-3 py-2 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors text-text-secondary hover:text-text-primary flex items-center gap-1.5 shadow-sm" title="Guía explicativa: Presupuesto Fijo vs. Horas Reales">
            ${getIcon('help-circle', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Ayuda: Modalidades & Carga</span>
          </button>
          <button id="btn-open-new-project-modal" class="px-3.5 py-2 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle transition-colors text-text-primary">
            + Nuevo Proyecto
          </button>
          <button id="btn-open-log-subtask-modal" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-all shadow-sm flex items-center gap-2">
            ${getIcon('plus', 'w-3.5 h-3.5')}
            <span>Cargar Subtarea / Horas</span>
          </button>
        </div>
      </div>

      <!-- Active Timer Widget -->
      <div class="bg-surface p-4 rounded-md border border-border shadow-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="flex items-center gap-3">
          <span id="timer-status-dot" class="w-3 h-3 rounded-full ${TimerService.isRunning() ? 'bg-red-500 animate-ping' : 'bg-zinc-300'}"></span>
          <div>
            <span class="text-xs font-semibold text-text-primary">Cronómetro Auxiliar</span>
            <p class="text-[11px] text-text-secondary" id="timer-task-label">
              ${TimerService.getProjectName() || (selectedProject ? selectedProject.name : 'Selecciona un proyecto')}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <span class="text-xl font-mono font-bold text-text-primary" id="timer-display">${TimerService.getFormattedTime()}</span>
          <div class="flex items-center gap-2">
            <button id="btn-timer-toggle" class="px-3 py-1.5 rounded-md text-xs font-semibold border border-border bg-surface-subtle hover:bg-border/40 text-text-primary transition-colors flex items-center gap-1.5">
              <span id="btn-timer-icon">${getIcon(TimerService.isRunning() ? 'pause' : 'play', 'w-3.5 h-3.5')}</span>
              <span id="btn-timer-text">${TimerService.isRunning() ? 'Pausar' : 'Iniciar'}</span>
            </button>
            <button id="btn-timer-reset" class="px-3 py-1.5 rounded-md text-xs font-semibold border border-border bg-surface hover:bg-surface-subtle text-text-secondary transition-colors">
              Reset
            </button>
            <button id="btn-timer-log" class="px-3 py-1.5 rounded-md text-xs font-semibold bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-colors flex items-center gap-1.5 shadow-sm" title="Imputar el tiempo medido a una subtarea">
              ${getIcon('plus', 'w-3.5 h-3.5')}
              <span>Imputar Horas</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Layout: Projects (35%) vs WBS & Details (65%) -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <!-- Projects Column (4 cols - Sticky) -->
        <div class="lg:col-span-4 lg:sticky lg:top-20 space-y-3 flex flex-col">
          <div class="flex items-center justify-between px-1">
            <span class="text-xs font-bold uppercase tracking-wider text-text-secondary">Listado de Proyectos</span>
            <span id="projects-count-label" class="text-xs font-mono text-text-secondary">
              ${filteredProjects.length === projects.length ? `${projects.length} totales` : `${filteredProjects.length} de ${projects.length}`}
            </span>
          </div>

          <!-- Project Search & Stage Filter -->
          <div class="space-y-2">
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
                ${getIcon('search', 'w-3.5 h-3.5')}
              </span>
              <input 
                type="text" 
                id="input-search-projects" 
                placeholder="Buscar proyecto o cliente..." 
                value="${projectSearchTerm}" 
                class="w-full pl-8 pr-8 py-1.5 text-xs bg-surface border border-border rounded-md text-text-primary placeholder:text-text-secondary/60 focus:ring-2 focus:ring-primary outline-none shadow-sm transition-all"
              />
              <button id="btn-clear-project-search" style="${projectSearchTerm ? 'display:flex' : 'display:none'}" class="absolute inset-y-0 right-0 pr-2.5 items-center text-text-secondary hover:text-text-primary text-xs" title="Limpiar búsqueda">
                ✕
              </button>
            </div>

            <!-- Stage Filter Dropdown -->
            <select id="select-filter-project-status" class="w-full text-xs bg-surface border border-border rounded-md px-2.5 py-1.5 text-text-primary focus:ring-2 focus:ring-primary outline-none shadow-sm cursor-pointer">
              <option value="all" ${projectStatusFilter === 'all' ? 'selected' : ''}>Todas las etapas</option>
              <option value="En curso" ${projectStatusFilter === 'En curso' ? 'selected' : ''}>En curso</option>
              <option value="Completado" ${projectStatusFilter === 'Completado' ? 'selected' : ''}>Completado</option>
              <option value="En pausa" ${projectStatusFilter === 'En pausa' ? 'selected' : ''}>En pausa</option>
              <option value="No iniciado" ${projectStatusFilter === 'No iniciado' ? 'selected' : ''}>No iniciado</option>
              <option value="Cancelado" ${projectStatusFilter === 'Cancelado' ? 'selected' : ''}>Cancelado</option>
            </select>
          </div>

          <!-- Scrollable project cards container -->
          <div id="project-cards-container" class="space-y-2 max-h-[calc(100vh-14rem)] overflow-y-auto pr-1">
            ${filteredProjects.length === 0 ? `
              <div class="p-6 text-center text-xs text-text-secondary border border-dashed border-border rounded-md bg-surface-subtle/40">
                ${projectSearchTerm || projectStatusFilter !== 'all' ? 'No hay proyectos que coincidan con los filtros aplicados' : 'Sin proyectos disponibles'}
              </div>
            ` : filteredProjects.map(p => renderProjectCardHtml(p, selectedProject && selectedProject.id === p.id)).join('')}
          </div>
        </div>

        <!-- WBS & Subtask Hierarchy Column (8 cols) -->
        <div class="lg:col-span-8 space-y-5">
          ${!selectedProject ? `
            <div class="bg-surface rounded-md border border-border p-12 text-center text-text-secondary">
              Selecciona un proyecto para ver su desglose.
            </div>
          ` : `
            <!-- Project Header Summary Card -->
            <div class="bg-surface rounded-md border border-border shadow-card p-6 space-y-4">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-border">
                <div>
                  <div class="flex items-center gap-2 flex-wrap">
                    <h2 class="text-xl font-bold text-text-primary">${selectedProject.name}</h2>
                    ${getProjectStatusBadge(selectedProject.status)}
                    <span class="text-xs font-mono px-2 py-0.5 rounded bg-surface-subtle border border-border">
                      ID: ${selectedProject.legacy_id || selectedProject.id}
                    </span>
                  </div>
                  <p class="text-xs text-text-secondary mt-0.5">
                    Cliente: <b>${selectedProject.client?.name || 'N/A'}</b> (${selectedProject.client?.channel || 'Directo'})
                  </p>
                </div>
                <div class="flex items-center gap-2 self-start flex-wrap">
                  <button id="btn-open-print-modal" class="px-3 py-1.5 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle text-text-primary transition-colors flex items-center gap-1.5 shadow-sm" title="Imprimir o guardar PDF A4">
                    ${getIcon('printer', 'w-3.5 h-3.5 text-text-secondary')}
                    <span>${selectedProject.billing_mode === 'time_and_materials' ? 'Remito / Horas PDF' : 'Presupuesto PDF'}</span>
                  </button>
                  <button id="btn-edit-project" class="px-3 py-1.5 text-xs font-semibold rounded-md border border-border bg-surface hover:bg-surface-subtle text-text-primary transition-colors flex items-center gap-1.5 shadow-sm">
                    ${getIcon('edit', 'w-3.5 h-3.5')}
                    <span>Editar</span>
                  </button>
                  <button id="btn-add-task-to-project" class="px-3 py-1.5 text-xs font-semibold rounded-md border border-border bg-surface-subtle hover:bg-border/40 text-text-primary transition-colors flex items-center gap-1.5 shadow-sm">
                    ${getIcon('plus', 'w-3.5 h-3.5')}
                    <span>Añadir Tarea</span>
                  </button>
                </div>
              </div>

              <!-- Date Range Filter Bar for Project Details -->
              <div class="p-3 bg-surface-subtle/80 rounded-md border border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs">
                <div class="flex items-center gap-2 flex-wrap">
                  <div class="flex items-center gap-1.5 font-semibold text-text-primary">
                    ${getIcon('calendar', 'w-3.5 h-3.5 text-primary')}
                    <span>Período:</span>
                  </div>
                  <div class="flex items-center gap-1.5">
                    <label class="text-[11px] text-text-secondary" for="filter-proj-date-from">Desde</label>
                    <input 
                      type="date" 
                      id="filter-proj-date-from" 
                      value="${projectFilterDateFrom}" 
                      class="border border-border rounded px-2 py-1 bg-surface text-text-primary font-mono text-xs outline-none focus:ring-1 focus:ring-primary shadow-2xs"
                    />
                  </div>
                  <div class="flex items-center gap-1.5">
                    <label class="text-[11px] text-text-secondary" for="filter-proj-date-to">Hasta</label>
                    <input 
                      type="date" 
                      id="filter-proj-date-to" 
                      value="${projectFilterDateTo}" 
                      class="border border-border rounded px-2 py-1 bg-surface text-text-primary font-mono text-xs outline-none focus:ring-1 focus:ring-primary shadow-2xs"
                    />
                  </div>
                  ${periodMetrics.hasFilter ? `
                    <button 
                      type="button" 
                      id="btn-clear-proj-date-filter" 
                      class="px-2 py-1 text-[11px] font-semibold text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded transition-colors flex items-center gap-1 shadow-2xs"
                      title="Quitar filtro de fechas y ver histórico"
                    >
                      ✕ Limpiar
                    </button>
                  ` : ''}
                </div>

                <!-- Presets -->
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span class="text-[10px] uppercase font-bold text-text-secondary hidden sm:inline">Rápido:</span>
                  <button type="button" class="btn-proj-date-preset px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors shadow-2xs" data-preset="all">
                    Histórico
                  </button>
                  <button type="button" class="btn-proj-date-preset px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors shadow-2xs" data-preset="this-month">
                    Este Mes
                  </button>
                  <button type="button" class="btn-proj-date-preset px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors shadow-2xs" data-preset="last-month">
                    Mes Anterior
                  </button>
                  <button type="button" class="btn-proj-date-preset px-2 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors shadow-2xs" data-preset="this-year">
                    Este Año
                  </button>
                </div>
              </div>

              <!-- Quick Metrics Grid -->
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono">
                <div class="p-3 bg-surface-subtle rounded border border-border flex flex-col justify-between">
                  <div class="flex items-center justify-between">
                    <span class="text-text-secondary block text-[10px] uppercase">Modalidad & Tipo</span>
                    <button type="button" class="btn-open-billing-help text-text-secondary hover:text-text-primary transition-colors" title="Ver cómo funciona esta modalidad">
                      ${getIcon('help-circle', 'w-3.5 h-3.5')}
                    </button>
                  </div>
                  <span class="font-bold text-text-primary mt-1 block">
                    ${selectedProject.revenue_type === 'recurrente' 
                      ? 'Recurrente (Abono)' 
                      : selectedProject.billing_mode === 'time_and_materials' 
                        ? 'Puntual (Horas Reales)' 
                        : 'Puntual (Presup. Fijo)'}
                  </span>
                </div>

                <div class="p-3 bg-surface-subtle rounded border border-border">
                  <span class="text-text-secondary block text-[10px] uppercase">Tarifa Aplicada</span>
                  <span class="font-bold text-text-primary mt-1 block">
                    ${selectedProject.rate?.name || 'Local'} (${formatMoney(selectedProject.rate?.hourly_rate, selectedProject.rate?.currency)}/h)
                  </span>
                </div>

                <!-- Prominent Financial Metric Card -->
                <div class="p-3 bg-surface-subtle rounded border border-border">
                  <div class="flex items-center justify-between">
                    <span class="text-text-secondary block text-[10px] uppercase">
                      ${selectedProject.revenue_type === 'recurrente'
                        ? (periodMetrics.hasFilter ? 'Devengado Período' : 'Abono Mensual Recurrente')
                        : selectedProject.billing_mode === 'time_and_materials' 
                          ? (periodMetrics.hasFilter ? 'Devengado Período' : 'Total Devengado por Horas') 
                          : (periodMetrics.hasFilter ? 'Devengado Período' : 'Presupuesto Devengado')}
                    </span>
                    ${periodMetrics.hasFilter ? `
                      <span class="text-[9px] font-mono px-1 py-0.2 rounded bg-primary/10 text-primary border border-primary/20 font-bold">Filtrado</span>
                    ` : ''}
                  </div>
                  <span class="font-bold text-sm mt-1 block font-mono ${selectedProject.revenue_type === 'recurrente' ? 'text-purple-700' : selectedProject.billing_mode === 'time_and_materials' ? 'text-emerald-600' : 'text-text-primary'}">
                    ${periodMetrics.hasFilter
                      ? formatMoney(periodMetrics.periodDevengado, selectedProject.rate?.currency || selectedProject.currency)
                      : (selectedProject.revenue_type === 'recurrente'
                          ? `${formatMoney(selectedProject.budget_amount, selectedProject.currency)} /mes`
                          : selectedProject.billing_mode === 'time_and_materials' 
                            ? `${formatMoney(selectedProject.billable_amount, selectedProject.rate?.currency)}`
                            : `${formatMoney(selectedProject.budget_amount, selectedProject.currency)}`)}
                  </span>
                  ${periodMetrics.hasFilter ? `
                    <span class="text-[10px] font-mono text-text-secondary block mt-0.5">
                      Histórico total: <b>${formatMoney(selectedProject.billable_amount || selectedProject.budget_amount || 0, selectedProject.rate?.currency || selectedProject.currency)}</b>
                    </span>
                  ` : (selectedProject.discount_percent > 0 ? `
                    <span class="text-[10px] font-mono text-emerald-700 font-medium">-${selectedProject.discount_percent}% desc. aplicado</span>
                  ` : '')}
                </div>

                <!-- Hours Tracking Card -->
                <div class="p-3 bg-surface-subtle rounded border border-border">
                  <div class="flex items-center justify-between">
                    <span class="text-text-secondary block text-[10px] uppercase">
                      ${periodMetrics.hasFilter ? 'Horas Período' : (selectedProject.billing_mode === 'time_and_materials' ? 'Horas Reales' : 'Horas (Reales / Presup)')}
                    </span>
                    ${periodMetrics.hasFilter ? `
                      <span class="text-[9px] font-mono px-1 py-0.2 rounded bg-primary/10 text-primary border border-primary/20 font-bold">Filtrado</span>
                    ` : ''}
                  </div>
                  <span class="font-bold mt-1 block font-mono ${selectedProject.is_over_budget && !periodMetrics.hasFilter ? 'text-red-600' : 'text-text-primary'}">
                    ${periodMetrics.hasFilter
                      ? `${formatHours(periodMetrics.periodActualHours)}`
                      : (selectedProject.billing_mode === 'time_and_materials'
                          ? `${formatHours(selectedProject.total_actual_hours)}`
                          : `${formatHours(selectedProject.total_actual_hours)} / ${selectedProject.budget_hours || 0}h`)}
                  </span>
                  ${periodMetrics.hasFilter ? `
                    <span class="text-[10px] font-mono text-text-secondary block mt-0.5">
                      Histórico total: <b>${formatHours(selectedProject.total_actual_hours)}</b>
                    </span>
                  ` : ''}
                </div>
              </div>

              <!-- Fixed Budget Consumption & "Trabajando Gratis" Warning Banner -->
              ${selectedProject.billing_mode === 'fixed_budget' ? `
                ${selectedProject.is_over_budget ? `
                  <div class="p-3.5 bg-red-50 border border-red-300 rounded-md text-red-900 flex items-start gap-3 animate-fade-in">
                    <span class="text-red-600 shrink-0 mt-0.5">${getIcon('alertCircle', 'w-5 h-5')}</span>
                    <div class="text-xs space-y-1">
                      <p class="font-bold text-red-800 text-sm">¡Alerta: Presupuesto Excedido! (Trabajando sin remuneración adicional)</p>
                      <p class="text-red-700 text-[11px] leading-relaxed">
                        Has consumido <b>${formatHours(selectedProject.total_actual_hours)}</b> sobre un presupuesto pactado de <b>${selectedProject.budget_hours || 0} hrs</b> (${selectedProject.budget_consumption_percent?.toFixed(0)}% del total, con <b>${formatHours(selectedProject.over_budget_hours)}</b> de exceso no remunerado).
                        Como este proyecto fue cotizado a <b>monto cerrado (${formatMoney(selectedProject.budget_amount, selectedProject.currency)})</b>, el costo de las horas adicionales lo absorbe el equipo. Para cobrar horas extras se requiere pactar una ampliación de alcance con el cliente.
                      </p>
                    </div>
                  </div>
                ` : `
                  <div class="p-3 bg-surface-subtle rounded border border-border space-y-1.5">
                    <div class="flex items-center justify-between text-xs font-mono">
                      <span class="text-text-secondary font-medium">Consumo de Presupuesto Horario:</span>
                      <span class="font-bold ${selectedProject.budget_consumption_percent >= 75 ? 'text-amber-700' : 'text-emerald-700'}">
                        ${selectedProject.budget_consumption_percent?.toFixed(1) || 0}% (${formatHours(selectedProject.total_actual_hours)} de ${selectedProject.budget_hours || 0} hrs)
                      </span>
                    </div>
                    <div class="w-full bg-zinc-200 rounded-full h-2 overflow-hidden">
                      <div class="h-full rounded-full transition-all ${
                        selectedProject.budget_consumption_percent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'
                      }" style="width: ${Math.min(100, selectedProject.budget_consumption_percent || 0)}%"></div>
                    </div>
                    <div class="flex items-center justify-between text-[11px] text-text-secondary">
                      <span>Horas restantes disponibles: <b>${formatHours(Math.max(0, (selectedProject.budget_hours || 0) - selectedProject.total_actual_hours))}</b></span>
                      <span>Monto cerrado contratado: <b>${formatMoney(selectedProject.budget_amount, selectedProject.currency)}</b></span>
                    </div>
                  </div>
                `}
              ` : `
                <!-- T&M summary banner -->
                <div class="p-3 bg-blue-50/60 border border-blue-200 rounded-md flex items-center justify-between text-xs text-blue-900">
                  <div class="flex items-center gap-2">
                    ${getIcon('clock', 'w-4 h-4 text-blue-600')}
                    <span>
                      ${periodMetrics.hasFilter
                        ? `Horas del período: <b>${formatHours(periodMetrics.periodActualHours)}</b> a tarifa de proyecto`
                        : `Liquidación acumulada: <b>${formatHours(selectedProject.total_actual_hours)}</b> a ${formatMoney(selectedProject.rate?.hourly_rate, selectedProject.rate?.currency)}/h`}
                    </span>
                  </div>
                  <span class="font-bold font-mono text-sm text-blue-950">
                    ${formatMoney(periodMetrics.hasFilter ? periodMetrics.periodDevengado : selectedProject.billable_amount, selectedProject.rate?.currency)}
                  </span>
                </div>
              `}

              ${selectedProject.description ? `
                <p class="text-xs text-text-secondary italic bg-surface-subtle/50 p-2.5 rounded border border-border/60">
                  "${selectedProject.description}"
                </p>
              ` : ''}
            </div>

            <!-- Task & Subtask Tree (WBS) -->
            ${(() => {
              const filteredTasks = getFilteredTasksForSelectedProject();

              return `
                <div class="bg-surface rounded-md border border-border shadow-card p-6 space-y-4">
                  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-border">
                    <div>
                      <h3 class="font-bold text-sm text-text-primary">Estructura WBS: Tareas & Subtareas</h3>
                      <span class="text-xs font-mono text-text-secondary" id="wbs-completed-tasks-badge">
                        ${filteredTasks.filter(t => t.status === 'Completada').length} de ${filteredTasks.length} tareas ${periodMetrics.hasFilter ? 'en período' : 'completadas'}
                      </span>
                    </div>

                    <!-- WBS Search Box, Subtask Sorting & Collapse Controls -->
                    <div class="flex items-center gap-2 flex-wrap">
                      <div class="relative min-w-[180px] sm:min-w-[220px]">
                        <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
                          ${getIcon('search', 'w-3 h-3')}
                        </span>
                        <input 
                          type="text" 
                          id="input-search-wbs" 
                          placeholder="Buscar tarea o subtarea..." 
                          value="${wbsSearchTerm}" 
                          class="w-full pl-7 pr-7 py-1 text-xs bg-surface-subtle border border-border rounded-md text-text-primary placeholder:text-text-secondary/60 focus:bg-surface focus:ring-2 focus:ring-primary outline-none transition-all"
                        />
                        ${wbsSearchTerm ? `
                          <button id="btn-clear-wbs-search" class="absolute inset-y-0 right-0 pr-2 flex items-center text-text-secondary hover:text-text-primary text-xs" title="Limpiar">✕</button>
                        ` : ''}
                      </div>

                      <!-- Subtasks Date Sorting Selector -->
                      <div class="flex items-center gap-1.5" title="Ordenar subtareas dentro de los cards por fecha">
                        <span class="text-text-secondary hidden md:inline">${getIcon('arrowUpDown', 'w-3.5 h-3.5')}</span>
                        <select 
                          id="select-subtask-sort" 
                          class="px-2 py-1 text-[11px] font-semibold border border-border rounded bg-surface text-text-primary hover:bg-surface-subtle focus:ring-1 focus:ring-primary outline-none transition-colors cursor-pointer"
                        >
                          <option value="desc" ${subtaskSortOrder === 'desc' ? 'selected' : ''}>Fecha: Recientes ↓</option>
                          <option value="asc" ${subtaskSortOrder === 'asc' ? 'selected' : ''}>Fecha: Antiguas ↑</option>
                        </select>
                      </div>

                      <button id="btn-collapse-all-tasks" class="px-2 py-1 text-[11px] font-semibold border border-border rounded bg-surface hover:bg-surface-subtle text-text-secondary transition-colors" title="Plegar todas las tareas">
                        Plegar
                      </button>
                      <button id="btn-expand-all-tasks" class="px-2 py-1 text-[11px] font-semibold border border-border rounded bg-surface hover:bg-surface-subtle text-text-secondary transition-colors" title="Desplegar todas las tareas">
                        Expandir
                      </button>
                    </div>
                  </div>

                  <div id="wbs-tasks-container" class="space-y-4">
                    ${filteredTasks.length === 0 ? `
                      <div class="py-10 text-center text-text-secondary text-sm border border-dashed border-border rounded-md">
                        ${wbsSearchTerm ? `No hay tareas o subtareas que coincidan con "${wbsSearchTerm}"` : 'Este proyecto aún no tiene tareas creadas. Presiona "+ Añadir Tarea" para empezar.'}
                      </div>
                    ` : filteredTasks.map(task => renderTaskCardHtml(task, collapsedTaskIds.has(task.id), wbsSearchTerm)).join('')}
                  </div>
                </div>
              `;
            })()}
          `}
        </div>
      </div>
    </div>
    </div> <!-- Close #projects-main-content -->

    <!-- Modal Cargar Subtarea / Horas -->
    <div id="modal-log-subtask" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-lg w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Registrar Horas en Subtarea</h3>
          <button id="btn-close-subtask-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-log-subtask" class="space-y-4">
          <!-- Selector: Proyecto de Cliente vs Hora Interna -->
          <div class="flex items-center gap-2 p-1 bg-surface-subtle border border-border rounded-md">
            <button type="button" id="btn-toggle-time-client" class="flex-1 py-1.5 text-xs font-semibold rounded bg-white text-text-primary shadow-sm transition-all text-center">
              Proyecto de Cliente
            </button>
            <button type="button" id="btn-toggle-time-internal" class="flex-1 py-1.5 text-xs font-semibold rounded text-text-secondary hover:text-text-primary transition-all text-center">
              Hora Interna (Admin / Ventas / Equipo Alerce)
            </button>
          </div>
          <input type="hidden" id="modal-time-type" value="client" />

          <!-- Bloque para Proyecto de Cliente -->
          <div id="container-client-time-fields" class="space-y-4">
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-semibold text-text-primary">Proyecto</label>
                <button type="button" class="btn-open-billing-help text-[11px] text-text-secondary hover:text-text-primary underline flex items-center gap-1">
                  ${getIcon('help-circle', 'w-3 h-3')}
                  <span>¿Presupuesto Fijo o Horas Reales?</span>
                </button>
              </div>
              <select id="modal-select-project" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                ${projects.map(p => `<option value="${p.id}" ${selectedProject && selectedProject.id === p.id ? 'selected' : ''}>${p.name}</option>`).join('')}
              </select>
              <!-- Dynamic Modalidad indicator container -->
              <div id="subtask-modal-billing-info" class="mt-2.5">
                <!-- Populated dynamically based on selected project -->
              </div>
            </div>

            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Tarea Asociada</label>
              <select id="modal-select-task" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <!-- Populated dynamically -->
              </select>
            </div>
          </div>

          <!-- Bloque para Hora Interna -->
          <div id="container-internal-time-fields" class="space-y-3 hidden">
            <div class="p-3 bg-amber-50/60 border border-amber-200 rounded-md text-xs text-amber-900 space-y-1">
              <div class="font-bold flex items-center gap-1.5">
                ${getIcon('info', 'w-3.5 h-3.5 text-amber-700')}
                <span>Registro de Tiempo Interno</span>
              </div>
              <p class="text-[11px] text-amber-800">
                Estas horas se computan para auditar el <b>Valor Real de tu Hora</b> sin facturar a ningún cliente externo.
              </p>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Categoría Interna</label>
              <select id="modal-select-internal-category" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="admin">Administración & Gestión General</option>
                <option value="ventas">Ventas, Prospección & Comercial</option>
                <option value="desarrollo">Desarrollo Propio & MicroSaaS Equipo Alerce</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha</label>
              <input type="date" id="modal-input-date" value="${new Date().toISOString().split('T')[0]}" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Horas Reales (ej: 1.5 o 0.75)</label>
              <input type="number" step="0.25" min="0.25" id="modal-input-hours" placeholder="1.0" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Descripción del Trabajo Realizado (Subtarea)</label>
            <textarea id="modal-input-desc" rows="3" placeholder="Detalle técnico de lo implementado o resuelto..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-subtask" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Subtarea
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Nuevo Proyecto -->
    <div id="modal-new-project" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-lg w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Crear Nuevo Proyecto</h3>
          <button id="btn-close-project-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-new-project" class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Nombre del Proyecto</label>
            <input type="text" id="modal-proj-name" placeholder="Ej: Sistema Automatización WhatsApp" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Cliente</label>
            <select id="modal-proj-client" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
              ${clients.map(c => `<option value="${c.id}">${c.name} (${c.channel || 'Directo'})</option>`).join('')}
            </select>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-semibold text-text-primary">Facturación</label>
                <button type="button" class="btn-open-billing-help text-[10px] text-text-secondary hover:text-text-primary underline">¿Ayuda?</button>
              </div>
              <select id="modal-proj-billing" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="fixed_budget">Presupuesto Fijo</option>
                <option value="time_and_materials">Por Horas Reales (T&M)</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Tipo de Ingreso</label>
              <select id="modal-proj-revenue-type" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="puntual">Puntual (Servicio único)</option>
                <option value="recurrente">Recurrente (Abono Mensual Fijo)</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Tarifa Horaria</label>
              <select id="modal-proj-rate" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                ${rates.map(r => `<option value="${r.id}">${r.name} (${r.hourly_rate} ${r.currency})</option>`).join('')}
              </select>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Presupuesto Horas</label>
              <input type="number" step="0.25" id="modal-proj-hours" placeholder="0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Descuento (%)</label>
              <input type="number" step="1" min="0" max="100" id="modal-proj-discount" placeholder="0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Presupuesto Monto ($)</label>
              <input type="number" step="any" id="modal-proj-amount" placeholder="0" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
          </div>
          <p class="text-[11px] text-text-secondary italic -mt-2">
            <b>Nota:</b> Para proyectos recurrentes, el monto ingresado representa el abono mensual fijo que se devengará automáticamente cada mes.
          </p>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Descripción Breve</label>
            <textarea id="modal-proj-desc" rows="2" placeholder="Objetivo y especificaciones generales..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none"></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-project" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Crear Proyecto
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Añadir Tarea -->
    <div id="modal-add-task" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-md w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Nueva Tarea en Proyecto</h3>
          <button id="btn-close-task-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-add-task" class="space-y-4">
          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Título de la Tarea</label>
            <input type="text" id="modal-task-title" placeholder="Ej: Integración de Pasarela de Pagos" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Prioridad</label>
              <select id="modal-task-priority" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="Media">Media</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
                <option value="Baja">Baja</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Horas Estimadas</label>
              <input type="number" step="0.25" id="modal-task-hours" placeholder="4.0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha de Inicio</label>
              <input type="date" id="modal-task-start-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha de Fin / Vence</label>
              <input type="date" id="modal-task-due-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-task" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Crear Tarea
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Editar Proyecto -->
    <div id="modal-edit-project" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-lg w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Editar Proyecto</h3>
          <button id="btn-close-edit-project-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-edit-project" class="space-y-4">
          <input type="hidden" id="edit-proj-id" value="" />

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Nombre del Proyecto</label>
            <input type="text" id="edit-proj-name" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Cliente</label>
              <select id="edit-proj-client" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                ${clients.map(c => `<option value="${c.id}">${c.name} (${c.channel || 'Directo'})</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Estado</label>
              <select id="edit-proj-status" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="En curso">En curso</option>
                <option value="Completado">Completado</option>
                <option value="En pausa">En pausa</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-semibold text-text-primary">Facturación</label>
                <button type="button" class="btn-open-billing-help text-[10px] text-text-secondary hover:text-text-primary underline">¿Ayuda?</button>
              </div>
              <select id="edit-proj-billing" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="fixed_budget">Presupuesto Fijo</option>
                <option value="time_and_materials">Por Horas Reales (T&M)</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Tipo de Ingreso</label>
              <select id="edit-proj-revenue-type" class="w-full text-xs font-medium border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="puntual">Puntual (Servicio único)</option>
                <option value="recurrente">Recurrente (Abono Mensual Fijo)</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Tarifa Horaria</label>
              <select id="edit-proj-rate" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                ${rates.map(r => `<option value="${r.id}">${r.name} (${r.hourly_rate} ${r.currency})</option>`).join('')}
              </select>
            </div>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Presupuesto Horas</label>
              <input type="number" step="0.25" id="edit-proj-hours" placeholder="0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Descuento (%)</label>
              <input type="number" step="1" min="0" max="100" id="edit-proj-discount" placeholder="0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Presupuesto Monto ($)</label>
              <input type="number" step="any" id="edit-proj-amount" placeholder="0" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
          </div>
          <p class="text-[11px] text-text-secondary italic -mt-2">
            El monto se autocompleta con <b>Horas × Tarifa</b> (aplicando descuento), pero puedes editarlo manualmente.
          </p>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Descripción Breve</label>
            <textarea id="edit-proj-desc" rows="2" placeholder="Objetivo y especificaciones generales..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none"></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-edit-project" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Editar Tarea -->
    <div id="modal-edit-task" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-md w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Editar Tarea</h3>
          <button id="btn-close-edit-task-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-edit-task" class="space-y-4">
          <input type="hidden" id="edit-task-id" value="" />

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Título de la Tarea</label>
            <input type="text" id="edit-task-title" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Prioridad</label>
              <select id="edit-task-priority" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="Media">Media</option>
                <option value="Alta">Alta</option>
                <option value="Urgente">Urgente</option>
                <option value="Baja">Baja</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Estado</label>
              <select id="edit-task-status" class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none">
                <option value="Pendiente">Pendiente</option>
                <option value="En curso">En curso</option>
                <option value="En revisión">En revisión</option>
                <option value="Completada">Completada</option>
              </select>
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Horas Estimadas</label>
            <input type="number" step="0.25" id="edit-task-hours" placeholder="4.0" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha de Inicio</label>
              <input type="date" id="edit-task-start-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha de Fin / Vence</label>
              <input type="date" id="edit-task-due-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" />
            </div>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-edit-task" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Tarea
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Editar Subtarea / Horas -->
    <div id="modal-edit-subtask" class="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 hidden flex items-center justify-center p-4 no-print">
      <div class="bg-surface rounded-md border border-border shadow-xl max-w-lg w-full p-6 space-y-4 animate-fade-in">
        <div class="flex items-center justify-between pb-3 border-b border-border">
          <h3 class="font-bold text-base text-text-primary">Editar Subtarea / Horas</h3>
          <button id="btn-close-edit-subtask-modal" class="text-text-secondary hover:text-text-primary text-sm font-bold">✕</button>
        </div>

        <form id="form-edit-subtask" class="space-y-4">
          <input type="hidden" id="edit-subtask-id" value="" />

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Fecha</label>
              <input type="date" id="edit-subtask-date" class="w-full text-xs font-mono border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
            <div>
              <label class="block text-xs font-semibold text-text-primary mb-1">Horas Reales</label>
              <input type="number" step="0.25" min="0.25" id="edit-subtask-hours" placeholder="1.0" class="w-full text-xs font-mono font-bold border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-text-primary mb-1">Descripción del Trabajo</label>
            <textarea id="edit-subtask-desc" rows="3" placeholder="Detalle técnico de lo implementado..." class="w-full text-xs border border-border rounded-md px-3 py-2 bg-surface text-text-primary focus:ring-2 focus:ring-primary outline-none" required></textarea>
          </div>

          <div class="pt-3 border-t border-border flex justify-end gap-2">
            <button type="button" id="btn-cancel-edit-subtask" class="px-4 py-2 text-xs font-semibold rounded-md border border-border hover:bg-surface-subtle text-text-secondary">
              Cancelar
            </button>
            <button type="submit" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active">
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de Ayuda Detallada: Presupuesto Fijo vs Horas Reales -->
    <div id="modal-billing-help" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 hidden flex items-center justify-center p-3 sm:p-4 overflow-y-auto no-print">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-2xl w-full my-auto animate-fade-in flex flex-col max-h-[90vh] overflow-hidden">
        
        <!-- Header -->
        <div class="p-4 sm:p-5 border-b border-border flex items-start justify-between gap-3 bg-surface-subtle/40">
          <div class="flex items-start gap-3">
            <div class="p-2 rounded-md bg-primary/10 text-primary border border-primary/20 shrink-0 mt-0.5">
              ${getIcon('help-circle', 'w-5 h-5')}
            </div>
            <div>
              <h3 class="font-bold text-base sm:text-lg text-text-primary leading-tight">
                Carga de Horas: Presupuesto Fijo vs. Horas Reales
              </h3>
              <p class="text-xs text-text-secondary mt-0.5">
                Cómo identificar la modalidad en Equipo Alerce Business OS y qué efecto tiene la registración de subtareas
              </p>
            </div>
          </div>
          <button id="btn-close-billing-help-modal" class="text-text-secondary hover:text-text-primary text-base font-bold p-1 leading-none" title="Cerrar modal">✕</button>
        </div>

        <!-- Scrollable Content Body -->
        <div class="p-5 sm:p-6 space-y-6 overflow-y-auto text-xs leading-relaxed">
          
          <!-- Bloque 1: ¿Dónde noto la diferencia en la pantalla? -->
          <div class="space-y-2.5">
            <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-primary"></span>
              1. ¿Cómo identificar la modalidad en la plataforma a simple vista?
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="p-3.5 rounded-md border border-border bg-surface-subtle/50 space-y-2">
                <div class="flex items-center justify-between">
                  <span class="font-bold text-text-primary">Proyecto con Presupuesto Fijo</span>
                  <span class="px-1.5 py-0.5 rounded font-medium text-[10px] bg-zinc-100 text-zinc-700 border border-zinc-200">Presupuesto Fijo</span>
                </div>
                <p class="text-text-secondary text-[11px] leading-relaxed">
                  • <b>En el Listado de Proyectos:</b> Verás la insignia gris neutra <code>Presupuesto Fijo</code> junto a la comparativa <code>Reales: Xh</code> vs <code>Presup: Yh</code>.<br/>
                  • <b>En las Métricas Superiores:</b> Se destaca el recuadro <b>Presupuesto Horas</b> y el presupuesto económico pactado.<br/>
                  • <b>En las Tareas WBS:</b> Cada tarea muestra sus <b>Horas Estimadas</b> y el indicador de <b>Desvío (%)</b> respecto a lo presupuestado.
                </p>
              </div>

              <div class="p-3.5 rounded-md border border-blue-200 bg-blue-50/40 space-y-2">
                <div class="flex items-center justify-between">
                  <span class="font-bold text-blue-900">Proyecto por Horas Reales (T&M)</span>
                  <span class="px-1.5 py-0.5 rounded font-medium text-[10px] bg-blue-50 text-blue-700 border border-blue-200">Por Horas Reales</span>
                </div>
                <p class="text-blue-900/90 text-[11px] leading-relaxed">
                  • <b>En el Listado de Proyectos:</b> Verás la insignia azul destacada <code>Por Horas Reales</code> y la tarifa horaria asociada.<br/>
                  • <b>En las Métricas Superiores:</b> Destaca la <b>Tarifa Aplicada</b> ($9.96 Local, $10.47 Exterior o $16.59 Fiverr).<br/>
                  • <b>En las Tareas WBS:</b> Las tareas actúan como agrupadores de trabajo técnico y cada subtarea acumula tiempo facturable.
                </p>
              </div>
            </div>
          </div>

          <!-- Bloque 2: Explicación Profunda de Carga de Horas -->
          <div class="space-y-2.5">
            <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-primary"></span>
              2. ¿Qué significa e implica cargar horas en cada modalidad?
            </h4>
            
            <div class="space-y-3">
              <div class="p-4 rounded-md border border-border bg-surface space-y-2">
                <div class="flex items-center gap-2 text-text-primary font-bold">
                  ${getIcon('check-circle', 'w-4 h-4 text-zinc-600')}
                  <span>A) Presupuesto Fijo (Fixed Budget): Control Interno & Desvíos</span>
                </div>
                <p class="text-text-secondary">
                  El cliente aprobó una cotización con un <b>precio total cerrado</b> y un volumen de horas estimadas de antemano.
                </p>
                <ul class="list-disc pl-4 space-y-1 text-text-secondary text-[11px]">
                  <li><b>Propósito de la carga:</b> Medir la productividad, costo interno y desvíos de Equipo Alerce.</li>
                  <li><b>Facturación al Cliente:</b> La carga de subtareas <b>NO altera</b> el valor final a facturar al cliente.</li>
                  <li><b>Lógica de Rentabilidad:</b> Si presupuestaste 20h y te llevó 15h, maximizaste margen de ganancia. Si te llevó 28h, tu rentabilidad horaria efectiva cayó (sobrecosto absorbido por Equipo Alerce, salvo ampliación de alcance).</li>
                  <li><b>En la WBS:</b> La barra de avance muestra el desvío porcentual respecto a la estimación previa de la tarea.</li>
                </ul>
              </div>

              <div class="p-4 rounded-md border border-blue-200 bg-blue-50/20 space-y-2">
                <div class="flex items-center gap-2 text-blue-900 font-bold">
                  ${getIcon('clock', 'w-4 h-4 text-blue-600')}
                  <span>B) Por Horas Reales (Time & Materials): Facturación Directa</span>
                </div>
                <p class="text-blue-950">
                  No hay un monto cerrado estricto. Se factura y cobra al cliente por el <b>tiempo real efectivamente trabajado</b>.
                </p>
                <ul class="list-disc pl-4 space-y-1 text-blue-900/90 text-[11px]">
                  <li><b>Propósito de la carga:</b> <b>Liquidación y cobro directo al cliente</b>.</li>
                  <li><b>Facturación al Cliente:</b> Cada subtarea cargada se traduce directamente en dinero: <code>Importe = Horas Reales × Tarifa Horaria del Canal</code>.</li>
                  <li><b>Estructura de Tarifas:</b> Se aplica la tarifa fijada para trasladar comisiones e impuestos (Fiverr ~24% retención bruta, Exterior Directo ~5% costos de intermediación, o Local Directo neto).</li>
                  <li><b>Importancia de la Bitácora:</b> El campo <i>Descripción</i> de la subtarea es fundamental: actúa como el reporte de horas detallado que respalda la liquidación y cobro ante el cliente.</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Bloque 3: Tabla Comparativa Rápida -->
          <div class="space-y-2">
            <h4 class="font-bold text-xs uppercase tracking-wider text-text-primary flex items-center gap-2">
              <span class="w-2 h-2 rounded-full bg-primary"></span>
              3. Cuadro Comparativo Resumen
            </h4>
            <div class="overflow-x-auto border border-border rounded-md">
              <table class="w-full text-left text-xs">
                <thead class="bg-surface-subtle font-semibold text-text-secondary border-b border-border">
                  <tr>
                    <th class="p-2.5">Criterio Operativo</th>
                    <th class="p-2.5">Presupuesto Fijo</th>
                    <th class="p-2.5 text-blue-800">Por Horas Reales (T&M)</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-border/60">
                  <tr>
                    <td class="p-2.5 font-medium text-text-primary">Distintivo Visual</td>
                    <td class="p-2.5"><span class="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200 text-zinc-700 text-[10px]">Presupuesto Fijo</span></td>
                    <td class="p-2.5"><span class="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 text-[10px]">Por Horas Reales</span></td>
                  </tr>
                  <tr>
                    <td class="p-2.5 font-medium text-text-primary">Destino de la Carga de Horas</td>
                    <td class="p-2.5 text-text-secondary">Control interno y cálculo de desvíos</td>
                    <td class="p-2.5 text-blue-900 font-medium">Facturación periódica a cobrar al cliente</td>
                  </tr>
                  <tr>
                    <td class="p-2.5 font-medium text-text-primary">Horas Estimadas de Tarea</td>
                    <td class="p-2.5 text-text-secondary">Límite contractual a no sobrepasar</td>
                    <td class="p-2.5 text-text-secondary">Referencia o estimación técnica flexible</td>
                  </tr>
                  <tr>
                    <td class="p-2.5 font-medium text-text-primary">Efecto si toma más tiempo</td>
                    <td class="p-2.5 text-amber-700 font-medium">Baja la rentabilidad por hora (absorbe Equipo Alerce)</td>
                    <td class="p-2.5 text-emerald-700 font-medium">Se traslada y factura más tiempo al cliente</td>
                  </tr>
                  <tr>
                    <td class="p-2.5 font-medium text-text-primary">Descripción de Subtarea</td>
                    <td class="p-2.5 text-text-secondary">Registro técnico interno de avance</td>
                    <td class="p-2.5 text-blue-900 font-medium">Respaldo auditable y comprobante para el cliente</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Bloque 4: Tips para Carga y Cronómetro -->
          <div class="p-4 rounded-md border border-border bg-surface-subtle/50 space-y-2">
            <h4 class="font-bold text-xs text-text-primary uppercase tracking-wider flex items-center gap-1.5">
              ${getIcon('zap', 'w-3.5 h-3.5 text-primary')}
              <span>Consejo Práctico: Carga Numérica y Cronómetro</span>
            </h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-text-secondary">
              <div>
                <p>• <b>Formato Decimal:</b> El sistema registra horas numéricas con decimales: <code>0.25</code> = 15m, <code>0.50</code> = 30m, <code>0.75</code> = 45m, <code>1.25</code> = 1h 15m.</p>
              </div>
              <div>
                <p>• <b>Cronómetro Integrado:</b> Puedes iniciar el cronómetro superior mientras trabajas y pulsar <b>"Imputar Horas"</b> para transferir el tiempo automáticamente al modal de subtarea.</p>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="p-4 border-t border-border flex justify-end bg-surface-subtle/40">
          <button id="btn-close-billing-help-footer" class="px-4 py-2 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary-hover active:bg-primary-active transition-colors">
            Entendido, volver a proyectos
          </button>
        </div>

      </div>
    </div>

    <!-- Modal Vista Previa de Impresión / Exportación PDF A4 -->
    <div id="modal-print-preview" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 hidden flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div class="bg-surface rounded-lg border border-border shadow-2xl max-w-4xl w-full my-auto animate-fade-in flex flex-col max-h-[95vh] overflow-hidden">
        
        <!-- Header Controls -->
        <div class="p-4 border-b border-border flex items-center justify-between bg-surface-subtle/50 no-print">
          <div class="flex items-center gap-2.5">
            <div class="p-1.5 rounded bg-primary/10 text-primary border border-primary/20">
              ${getIcon('printer', 'w-4 h-4')}
            </div>
            <div>
              <h3 class="font-bold text-sm text-text-primary" id="print-modal-title">
                ${selectedProject?.billing_mode === 'time_and_materials' ? 'Remito de Horas & Liquidación (A4)' : 'Presupuesto Comercial (A4)'}
              </h3>
              <p class="text-[11px] text-text-secondary">Vista previa y configuración antes de imprimir o guardar en PDF</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <button id="btn-download-pdf" class="px-3.5 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white transition-all shadow-sm flex items-center gap-1.5" title="Descargar archivo PDF A4 listo para enviar al cliente">
              ${getIcon('download', 'w-3.5 h-3.5')}
              <span>Descargar PDF</span>
            </button>
            <button id="btn-trigger-print" class="px-3.5 py-1.5 text-xs font-semibold rounded-md bg-primary hover:bg-primary-hover active:bg-primary-active text-white transition-all shadow-sm flex items-center gap-1.5" title="Imprimir documento oficial">
              ${getIcon('printer', 'w-3.5 h-3.5')}
              <span>Imprimir</span>
            </button>
            <button id="btn-close-print-modal" class="text-text-secondary hover:text-text-primary text-base font-bold p-1 leading-none ml-1" title="Cerrar">✕</button>
          </div>
        </div>

        <!-- Configuration Bar -->
        <div class="px-5 py-3 bg-surface border-b border-border space-y-3 text-xs no-print">
          ${selectedProject?.billing_mode === 'time_and_materials' ? `
            <!-- Period Filters & Quick Presets -->
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-bold text-text-primary flex items-center gap-1">
                  ${getIcon('calendar', 'w-3.5 h-3.5 text-text-secondary')}
                  Período:
                </span>
                <div class="flex items-center gap-1.5">
                  <label class="text-text-secondary text-[11px]" for="input-print-from">Desde</label>
                  <input type="date" id="input-print-from" class="border border-border rounded px-2 py-1 bg-surface text-text-primary font-mono text-xs outline-none" />
                </div>
                <div class="flex items-center gap-1.5">
                  <label class="text-text-secondary text-[11px]" for="input-print-to">Hasta</label>
                  <input type="date" id="input-print-to" class="border border-border rounded px-2 py-1 bg-surface text-text-primary font-mono text-xs outline-none" />
                </div>
              </div>

              <!-- Quick Presets -->
              <div class="flex items-center gap-1.5 flex-wrap">
                <button type="button" class="btn-period-quick px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-period="all">Todas</button>
                <button type="button" class="btn-period-quick px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-period="this-month">Este Mes</button>
                <button type="button" class="btn-period-quick px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-period="last-month">Mes Anterior</button>
                <button type="button" class="btn-period-quick px-2.5 py-1 text-[11px] font-medium rounded border border-border bg-surface hover:bg-surface-subtle text-text-secondary hover:text-text-primary transition-colors" data-period="last-15">Últimos 15 días</button>
              </div>
            </div>

            <!-- Subtask Selection Controls & Visibility Options -->
            <div class="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border/60">
              <div class="flex items-center gap-2 flex-wrap">
                <button type="button" id="btn-toggle-subtasks-drawer" class="px-2.5 py-1 text-xs font-semibold rounded border border-border bg-surface hover:bg-surface-subtle flex items-center gap-1.5 text-text-primary transition-colors shadow-sm">
                  ${getIcon('checkSquare', 'w-3.5 h-3.5 text-primary')}
                  <span id="label-subtasks-drawer">Elegir Subtareas a Imprimir</span>
                </button>
                <span id="print-selection-summary" class="font-mono text-xs text-text-primary font-bold bg-surface-subtle px-2 py-0.5 rounded border border-border">
                  Cargando selección...
                </span>
              </div>

              <div class="flex items-center gap-3 flex-wrap">
                <span class="text-text-secondary font-medium">Secciones:</span>
                <label class="flex items-center gap-1 cursor-pointer text-text-secondary hover:text-text-primary">
                  <input type="checkbox" id="chk-print-summary" checked class="rounded border-border text-primary focus:ring-primary" />
                  <span>Resumen</span>
                </label>
                <label class="flex items-center gap-1 cursor-pointer text-text-secondary hover:text-text-primary">
                  <input type="checkbox" id="chk-print-tasks" checked class="rounded border-border text-primary focus:ring-primary" />
                  <span>WBS</span>
                </label>
                <label class="flex items-center gap-1 cursor-pointer text-text-secondary hover:text-text-primary">
                  <input type="checkbox" id="chk-print-subtasks" checked class="rounded border-border text-primary focus:ring-primary" />
                  <span>Bitácora</span>
                </label>
              </div>
            </div>

            <!-- Collapsible Drawer for individual Subtask Selection -->
            <div id="drawer-subtasks-selector" class="hidden border border-border rounded-md bg-surface-subtle/70 p-3 space-y-2 max-h-56 overflow-y-auto animate-fade-in no-print">
              <div class="flex items-center justify-between pb-2 border-b border-border text-[11px]">
                <label class="flex items-center gap-2 font-bold cursor-pointer text-text-primary">
                  <input type="checkbox" id="chk-select-all-subtasks" checked class="rounded border-border text-primary" />
                  <span>Tildar / Destildar todas las tareas</span>
                </label>
                <span class="text-text-secondary italic">Selecciona solo las horas que no te hayan pagado aún</span>
              </div>
              <div id="list-subtasks-checkboxes" class="space-y-1 divide-y divide-border/40">
                <!-- Injected via JavaScript -->
              </div>
            </div>
          ` : `
            <div class="flex items-center gap-4 flex-wrap">
              <div class="flex items-center gap-2">
                <label class="font-semibold text-text-primary" for="select-print-validity">Plazo de Validez:</label>
                <select id="select-print-validity" class="border border-border rounded px-2.5 py-1 bg-surface text-text-primary text-xs outline-none">
                  <option value="15" ${selectedProject?.validity_days === 15 ? 'selected' : ''}>15 días corridos</option>
                  <option value="30" ${selectedProject?.validity_days === 30 ? 'selected' : ''}>30 días corridos</option>
                  <option value="45">45 días corridos</option>
                  <option value="60">60 días corridos</option>
                </select>
              </div>
              <div class="flex items-center gap-2">
                <label class="font-semibold text-text-primary" for="input-print-date">Fecha de Emisión:</label>
                <input type="date" id="input-print-date" value="${new Date().toISOString().split('T')[0]}" class="border border-border rounded px-2.5 py-1 bg-surface text-text-primary font-mono text-xs outline-none" />
              </div>
            </div>
          `}
        </div>

        <!-- Scrollable A4 Document Viewport: Hojas A4 Reales -->
        <div class="p-4 sm:p-6 overflow-y-auto bg-zinc-200/80 flex flex-col items-center flex-1 max-h-[75vh]">
          <div class="w-full max-w-[794px] mb-4 px-3.5 py-2 bg-zinc-800 text-zinc-200 text-xs font-mono rounded flex items-center justify-between no-print shadow-sm">
            <span class="flex items-center gap-2">
              ${getIcon('fileText', 'w-3.5 h-3.5 text-emerald-400')}
              <span class="font-sans font-medium text-[11px]">Hojas A4 Oficiales · Paginación exacta sin desbordes</span>
            </span>
            <span id="pdf-page-count-badge" class="bg-zinc-700 text-zinc-200 text-[11px] font-mono px-2.5 py-0.5 rounded font-bold">1 Página A4</span>
          </div>

          <!-- Contenedor de hojas A4 independientes -->
          <div id="printable-sheets-container" class="w-full flex flex-col items-center">
            <!-- Hojas .a4-sheet inyectadas dinámicamente -->
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="p-3 bg-surface-subtle border-t border-border flex items-center justify-between text-xs text-text-secondary no-print">
          <span class="font-mono">Formato: Estándar A4 (210 × 297 mm) · Monocromático Minimalista</span>
          <button id="btn-close-print-footer" class="px-3 py-1.5 border border-border rounded bg-surface hover:bg-surface-subtle text-text-secondary">
            Cerrar
          </button>
        </div>

      </div>
    </div>
  `;
}

export function initProjectsEvents() {
  // --- Modals References ---
  const subtaskModal = document.getElementById('modal-log-subtask');
  const projectModal = document.getElementById('modal-new-project');
  const taskModal = document.getElementById('modal-add-task');
  const editProjectModal = document.getElementById('modal-edit-project');
  const editTaskModal = document.getElementById('modal-edit-task');
  const editSubtaskModal = document.getElementById('modal-edit-subtask');
  const modalSelectProject = document.getElementById('modal-select-project');

  const openSubtaskModal = (targetTaskId = null, targetProjectId = null) => {
    if (!subtaskModal) return;
    subtaskModal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
    if (targetProjectId && modalSelectProject) {
      modalSelectProject.value = targetProjectId;
    }
    updateModalTaskOptions(targetTaskId);
  };

  const closeSubtaskModal = () => {
    if (!subtaskModal) return;
    subtaskModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    subtaskLoggedFromTimer = false;
  };

  const closeProjectModal = () => {
    if (projectModal) projectModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const closeTaskModal = () => {
    if (taskModal) taskModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const closeEditProjectModal = () => {
    if (editProjectModal) editProjectModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const closeEditTaskModal = () => {
    if (editTaskModal) editTaskModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const closeEditSubtaskModal = () => {
    if (editSubtaskModal) editSubtaskModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  // --- Project Search & Stage Filter (In-memory DOM update, preserves input focus) ---
  const inputSearchProj = document.getElementById('input-search-projects');
  const btnClearSearchProj = document.getElementById('btn-clear-project-search');
  const selectStatusFilter = document.getElementById('select-filter-project-status');
  const countLabelProj = document.getElementById('projects-count-label');
  const projectCardsContainer = document.getElementById('project-cards-container');

  const bindProjectCardClicks = () => {
    document.querySelectorAll('.project-card').forEach(card => {
      card.onclick = () => {
        selectedProjectId = Number(card.dataset.projectId);
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      };
    });
  };

  const updateProjectListInDOM = () => {
    const filtered = getFilteredProjects();
    if (countLabelProj) {
      countLabelProj.textContent = filtered.length === cachedProjects.length
        ? `${cachedProjects.length} totales`
        : `${filtered.length} de ${cachedProjects.length}`;
    }
    if (btnClearSearchProj) {
      btnClearSearchProj.style.display = projectSearchTerm ? 'flex' : 'none';
    }
    if (projectCardsContainer) {
      if (filtered.length === 0) {
        projectCardsContainer.innerHTML = `
          <div class="p-6 text-center text-xs text-text-secondary border border-dashed border-border rounded-md bg-surface-subtle/40">
            ${projectSearchTerm || projectStatusFilter !== 'all' ? 'No hay proyectos que coincidan con los filtros aplicados' : 'Sin proyectos disponibles'}
          </div>
        `;
      } else {
        projectCardsContainer.innerHTML = filtered.map(p =>
          renderProjectCardHtml(p, Number(selectedProjectId) === p.id)
        ).join('');
      }
      bindProjectCardClicks();
    }
  };

  let projDebounce = null;
  if (inputSearchProj) {
    inputSearchProj.oninput = (e) => {
      projectSearchTerm = e.target.value;
      clearTimeout(projDebounce);
      projDebounce = setTimeout(() => {
        updateProjectListInDOM();
      }, 150);
    };

    inputSearchProj.onkeydown = (e) => {
      if (e.key === 'Escape') {
        projectSearchTerm = '';
        inputSearchProj.value = '';
        updateProjectListInDOM();
      }
    };
  }

  if (btnClearSearchProj) {
    btnClearSearchProj.onclick = () => {
      projectSearchTerm = '';
      if (inputSearchProj) {
        inputSearchProj.value = '';
        inputSearchProj.focus();
      }
      updateProjectListInDOM();
    };
  }

  if (selectStatusFilter) {
    selectStatusFilter.onchange = (e) => {
      projectStatusFilter = e.target.value;
      updateProjectListInDOM();
    };
  }

  // --- Project Details Date Range Filter ---
  const inputFilterDateFrom = document.getElementById('filter-proj-date-from');
  const inputFilterDateTo = document.getElementById('filter-proj-date-to');
  const btnClearProjDate = document.getElementById('btn-clear-proj-date-filter');

  const onDateFilterChange = () => {
    projectFilterDateFrom = inputFilterDateFrom?.value || '';
    projectFilterDateTo = inputFilterDateTo?.value || '';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };

  if (inputFilterDateFrom) inputFilterDateFrom.onchange = onDateFilterChange;
  if (inputFilterDateTo) inputFilterDateTo.onchange = onDateFilterChange;

  if (btnClearProjDate) {
    btnClearProjDate.onclick = () => {
      projectFilterDateFrom = '';
      projectFilterDateTo = '';
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  }

  document.querySelectorAll('.btn-proj-date-preset').forEach(btn => {
    btn.onclick = () => {
      const preset = btn.dataset.preset;
      const { from, to } = getProjectDatePresetRange(preset);
      projectFilterDateFrom = from;
      projectFilterDateTo = to;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    };
  });

  // --- WBS Search & Interactive Collapsible Tasks (In-memory DOM update) ---
  const inputSearchWbs = document.getElementById('input-search-wbs');
  const btnClearSearchWbs = document.getElementById('btn-clear-wbs-search');
  const wbsContainer = document.getElementById('wbs-tasks-container');
  const btnCollapseAll = document.getElementById('btn-collapse-all-tasks');
  const btnExpandAll = document.getElementById('btn-expand-all-tasks');

  const toggleTaskCollapse = (taskId) => {
    const container = document.getElementById(`subtasks-container-${taskId}`);
    const chevron = document.getElementById(`task-chevron-${taskId}`);
    if (collapsedTaskIds.has(taskId)) {
      collapsedTaskIds.delete(taskId);
      if (container) container.classList.remove('hidden');
      if (chevron) chevron.innerHTML = getIcon('chevron-down', 'w-4 h-4');
    } else {
      collapsedTaskIds.add(taskId);
      if (container) container.classList.add('hidden');
      if (chevron) chevron.innerHTML = getIcon('chevron-right', 'w-4 h-4');
    }
  };

  const bindWbsTaskEvents = () => {
    // 1. Task header click toggles subtasks collapse
    document.querySelectorAll('.task-header-row').forEach(header => {
      header.onclick = () => {
        const taskId = Number(header.dataset.taskId);
        toggleTaskCollapse(taskId);
      };
    });

    // 2. Edit task button
    document.querySelectorAll('.btn-edit-task').forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        const taskId = Number(btn.dataset.taskId);
        const currentProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
        const task = (currentProj?.tasks || []).find(t => t.id === taskId);
        if (!task || !editTaskModal) return;

        document.getElementById('edit-task-id').value = task.id;
        document.getElementById('edit-task-title').value = task.title || '';
        document.getElementById('edit-task-priority').value = task.priority || 'Media';
        document.getElementById('edit-task-status').value = task.status || 'Pendiente';
        document.getElementById('edit-task-hours').value = task.estimated_hours || 0;
        if (document.getElementById('edit-task-start-date')) {
          document.getElementById('edit-task-start-date').value = task.start_date || '';
        }
        if (document.getElementById('edit-task-due-date')) {
          document.getElementById('edit-task-due-date').value = task.due_date || '';
        }

        editTaskModal.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
      };
    });

    // 3. Quick log subtask button on task card
    document.querySelectorAll('.btn-log-subtask-quick').forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        subtaskLoggedFromTimer = false;
        openSubtaskModal(Number(btn.dataset.taskId));
      };
    });

    // 4. Edit subtask button
    document.querySelectorAll('.btn-edit-subtask').forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        const subtaskId = Number(btn.dataset.subtaskId);
        const currentProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
        const subtask = (currentProj?.tasks || []).flatMap(t => t.subtasks || []).find(st => Number(st.id) === subtaskId);
        if (!subtask || !editSubtaskModal) return;

        document.getElementById('edit-subtask-id').value = subtask.id;
        document.getElementById('edit-subtask-date').value = subtask.date || '';
        document.getElementById('edit-subtask-hours').value = subtask.actual_hours || 1;
        document.getElementById('edit-subtask-desc').value = subtask.description || '';

        editSubtaskModal.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
      };
    });

    // 5. Delete subtask button
    document.querySelectorAll('.btn-delete-subtask').forEach(btn => {
      btn.onclick = async (e) => {
        e.stopPropagation();
        if (confirm('¿Eliminar esta subtarea y sus horas imputadas?')) {
          try {
            await DataService.deleteSubtask(btn.dataset.subtaskId);
            showToast('Subtarea eliminada');
            window.dispatchEvent(new HashChangeEvent('hashchange'));
          } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
          }
        }
      };
    });

    // 6. Subtask sort toggle button inside task cards
    document.querySelectorAll('.btn-toggle-subtask-sort').forEach(btn => {
      btn.onclick = (e) => {
        e.stopPropagation();
        subtaskSortOrder = subtaskSortOrder === 'desc' ? 'asc' : 'desc';
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('alerce_subtask_sort_order', subtaskSortOrder);
        }
        const select = document.getElementById('select-subtask-sort');
        if (select) select.value = subtaskSortOrder;
        updateWbsListInDOM();
      };
    });
  };

  const updateWbsListInDOM = () => {
    if (btnClearSearchWbs) {
      btnClearSearchWbs.style.display = wbsSearchTerm ? 'flex' : 'none';
    }
    if (!wbsContainer) return;

    const filteredTasks = getFilteredTasksForSelectedProject();
    const countBadge = document.getElementById('wbs-completed-tasks-badge');
    if (countBadge) {
      const hasFilter = Boolean(projectFilterDateFrom || projectFilterDateTo);
      countBadge.textContent = `${filteredTasks.filter(t => t.status === 'Completada').length} de ${filteredTasks.length} tareas ${hasFilter ? 'en período' : 'completadas'}`;
    }
    if (filteredTasks.length === 0) {
      wbsContainer.innerHTML = `
        <div class="py-10 text-center text-text-secondary text-sm border border-dashed border-border rounded-md">
          ${wbsSearchTerm ? `No hay tareas o subtareas que coincidan con "${wbsSearchTerm}"` : 'Este proyecto aún no tiene tareas creadas. Presiona "+ Añadir Tarea" para empezar.'}
        </div>
      `;
    } else {
      wbsContainer.innerHTML = filteredTasks.map(task =>
        renderTaskCardHtml(task, collapsedTaskIds.has(task.id), wbsSearchTerm)
      ).join('');
    }
    bindWbsTaskEvents();
  };

  let wbsDebounce = null;
  if (inputSearchWbs) {
    inputSearchWbs.oninput = (e) => {
      wbsSearchTerm = e.target.value;
      clearTimeout(wbsDebounce);
      wbsDebounce = setTimeout(() => {
        updateWbsListInDOM();
      }, 150);
    };

    inputSearchWbs.onkeydown = (e) => {
      if (e.key === 'Escape') {
        wbsSearchTerm = '';
        inputSearchWbs.value = '';
        updateWbsListInDOM();
      }
    };
  }

  if (btnClearSearchWbs) {
    btnClearSearchWbs.onclick = () => {
      wbsSearchTerm = '';
      if (inputSearchWbs) {
        inputSearchWbs.value = '';
        inputSearchWbs.focus();
      }
      updateWbsListInDOM();
    };
  }

  if (btnCollapseAll) {
    btnCollapseAll.onclick = () => {
      const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      (curProj?.tasks || []).forEach(t => {
        collapsedTaskIds.add(t.id);
        const container = document.getElementById(`subtasks-container-${t.id}`);
        const chevron = document.getElementById(`task-chevron-${t.id}`);
        if (container) container.classList.add('hidden');
        if (chevron) chevron.innerHTML = getIcon('chevron-right', 'w-4 h-4');
      });
    };
  }

  if (btnExpandAll) {
    btnExpandAll.onclick = () => {
      collapsedTaskIds.clear();
      const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      (curProj?.tasks || []).forEach(t => {
        const container = document.getElementById(`subtasks-container-${t.id}`);
        const chevron = document.getElementById(`task-chevron-${t.id}`);
        if (container) container.classList.remove('hidden');
        if (chevron) chevron.innerHTML = getIcon('chevron-down', 'w-4 h-4');
      });
    };
  }

  // WBS subtask date sort selector
  const selectSubtaskSort = document.getElementById('select-subtask-sort');
  if (selectSubtaskSort) {
    selectSubtaskSort.onchange = (e) => {
      subtaskSortOrder = e.target.value;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('alerce_subtask_sort_order', subtaskSortOrder);
      }
      updateWbsListInDOM();
    };
  }

  // Initial event bindings for projects and WBS
  bindProjectCardClicks();
  bindWbsTaskEvents();

  // --- Timer Controls ---
  if (timerUnsubscribe) {
    timerUnsubscribe();
    timerUnsubscribe = null;
  }

  const timerDisplay = document.getElementById('timer-display');
  const timerToggleBtn = document.getElementById('btn-timer-toggle');
  const timerText = document.getElementById('btn-timer-text');
  const timerIcon = document.getElementById('btn-timer-icon');
  const timerDot = document.getElementById('timer-status-dot');
  const timerResetBtn = document.getElementById('btn-timer-reset');
  const timerLogBtn = document.getElementById('btn-timer-log');
  const timerLabel = document.getElementById('timer-task-label');

  timerUnsubscribe = TimerService.subscribe(({ formatted, isRunning, projectName }) => {
    const curDisplay = document.getElementById('timer-display');
    if (!curDisplay) return;

    curDisplay.textContent = formatted;
    const curText = document.getElementById('btn-timer-text');
    if (curText) curText.textContent = isRunning ? 'Pausar' : 'Iniciar';
    const curIcon = document.getElementById('btn-timer-icon');
    if (curIcon) curIcon.innerHTML = getIcon(isRunning ? 'pause' : 'play', 'w-3.5 h-3.5');
    const curDot = document.getElementById('timer-status-dot');
    if (curDot) {
      curDot.className = `w-3 h-3 rounded-full ${isRunning ? 'bg-red-500 animate-ping' : 'bg-zinc-300'}`;
    }
    const curLabel = document.getElementById('timer-task-label');
    if (curLabel && projectName) {
      curLabel.textContent = projectName;
    }
  });

  if (timerToggleBtn) {
    timerToggleBtn.onclick = () => {
      if (TimerService.isRunning()) {
        TimerService.pause();
      } else {
        const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
        TimerService.start(selectedProjectId, curProj?.name || '');
      }
    };
  }

  if (timerResetBtn) {
    timerResetBtn.onclick = () => {
      TimerService.reset();
      const curProj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      const curLabel = document.getElementById('timer-task-label');
      if (curLabel) {
        curLabel.textContent = curProj ? curProj.name : 'Selecciona un proyecto';
      }
    };
  }

  if (timerLogBtn) {
    timerLogBtn.onclick = () => {
      const totalSecs = TimerService.getSeconds();
      if (totalSecs < 60) {
        showToast('Acumula al menos 1 minuto en el cronómetro para imputar', 'error');
        return;
      }
      if (TimerService.isRunning()) {
        TimerService.pause();
      }
      const hoursDecimal = Math.max(0.25, parseFloat((totalSecs / 3600).toFixed(2)));
      subtaskLoggedFromTimer = true;
      const targetProjId = TimerService.getProjectId() || selectedProjectId;
      openSubtaskModal(null, targetProjId);
      const hoursInput = document.getElementById('modal-input-hours');
      if (hoursInput) {
        hoursInput.value = hoursDecimal;
      }
      showToast(`Tiempo transferido: ${hoursDecimal}h. Selecciona la tarea y confirma.`);
    };
  }

  // Global event from layout "+ Cargar Horas"
  window.addEventListener('open-quick-time-modal', () => {
    subtaskLoggedFromTimer = false;
    openSubtaskModal();
  });

  // Auto-open modal if navigated via ?action=log
  if (window.location.hash.includes('action=log')) {
    setTimeout(() => {
      subtaskLoggedFromTimer = false;
      openSubtaskModal();
    }, 120);
  }

  const btnOpenLog = document.getElementById('btn-open-log-subtask-modal');
  if (btnOpenLog) {
    btnOpenLog.onclick = () => {
      subtaskLoggedFromTimer = false;
      openSubtaskModal();
    };
  }

  const btnCloseSubtask = document.getElementById('btn-close-subtask-modal');
  const btnCancelSubtask = document.getElementById('btn-cancel-subtask');
  [btnCloseSubtask, btnCancelSubtask].forEach(b => {
    if (b) b.onclick = () => closeSubtaskModal();
  });

  // Project Modal Buttons
  const btnOpenProj = document.getElementById('btn-open-new-project-modal');
  const btnCloseProj = document.getElementById('btn-close-project-modal');
  const btnCancelProj = document.getElementById('btn-cancel-project');
  if (btnOpenProj) {
    btnOpenProj.onclick = () => {
      projectModal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }
  [btnCloseProj, btnCancelProj].forEach(b => {
    if (b) b.onclick = () => closeProjectModal();
  });

  // Task Modal Buttons
  const btnOpenTask = document.getElementById('btn-add-task-to-project');
  const btnCloseTask = document.getElementById('btn-close-task-modal');
  const btnCancelTask = document.getElementById('btn-cancel-task');
  if (btnOpenTask) {
    btnOpenTask.onclick = () => {
      taskModal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }
  [btnCloseTask, btnCancelTask].forEach(b => {
    if (b) b.onclick = () => closeTaskModal();
  });

  // Dynamic task dropdown update when project changes in subtask modal
  if (modalSelectProject) {
    modalSelectProject.onchange = () => updateModalTaskOptions();
  }

  async function updateModalTaskOptions(selectedTaskId = null) {
    const projId = Number(modalSelectProject?.value || selectedProjectId);
    const proj = cachedProjects.find(p => p.id === projId);
    const billingInfoContainer = document.getElementById('subtask-modal-billing-info');
    if (billingInfoContainer && proj) {
      const isTAndM = proj.billing_mode === 'time_and_materials';
      billingInfoContainer.innerHTML = isTAndM ? `
        <div class="bg-blue-50/80 border border-blue-200 rounded p-2.5 text-xs text-blue-900 space-y-1">
          <div class="flex items-center justify-between font-semibold">
            <span class="flex items-center gap-1.5 text-blue-800">
              ${getIcon('clock', 'w-3.5 h-3.5 text-blue-600')}
              Modalidad: Por Horas Reales (T&M)
            </span>
            <span class="font-mono text-[10px] px-1.5 py-0.5 bg-white rounded border border-blue-200 text-blue-700 font-bold">
              ${proj.rate?.name || 'Tarifa'} (${formatMoney(proj.rate?.hourly_rate, proj.rate?.currency)}/h)
            </span>
          </div>
          <p class="text-[11px] text-blue-800/90 leading-tight">
            <b>Facturación Directa:</b> Esta subtarea es <u>facturable al cliente</u>. Las horas cargadas se liquidan multiplicadas por la tarifa pactada.
            <button type="button" class="btn-open-billing-help underline font-semibold ml-1 text-blue-900">¿Cómo funciona?</button>
          </p>
        </div>
      ` : `
        <div class="bg-surface-subtle border border-border rounded p-2.5 text-xs text-text-primary space-y-1">
          <div class="flex items-center justify-between font-semibold">
            <span class="flex items-center gap-1.5 text-text-primary">
              ${getIcon('check-circle', 'w-3.5 h-3.5 text-zinc-600')}
              Modalidad: Presupuesto Fijo
            </span>
            <span class="font-mono text-[10px] px-1.5 py-0.5 bg-surface rounded border border-border text-text-secondary">
              Presupuesto: ${proj.budget_hours || 0}h
            </span>
          </div>
          <p class="text-[11px] text-text-secondary leading-tight">
            <b>Control Interno:</b> La carga no altera el monto cerrado cotizado al cliente; sirve para auditar horas reales vs. estimadas y desvíos.
            <button type="button" class="btn-open-billing-help underline font-semibold ml-1 text-text-primary">¿Cómo funciona?</button>
          </p>
        </div>
      `;
    }

    const taskSelect = document.getElementById('modal-select-task');
    if (!taskSelect) return;

    taskSelect.innerHTML = '<option value="">Cargando tareas...</option>';
    const tasks = await DataService.getTasks(projId);
    if (tasks.length === 0) {
      taskSelect.innerHTML = '<option value="">Sin tareas en este proyecto (crea una primero)</option>';
    } else {
      taskSelect.innerHTML = tasks.map(t => `
        <option value="${t.id}" ${selectedTaskId === t.id ? 'selected' : ''}>${t.title}</option>
      `).join('');
    }
  }

  // --- Time Modal: Client vs Internal toggle ---
  const btnTimeClient = document.getElementById('btn-toggle-time-client');
  const btnTimeInternal = document.getElementById('btn-toggle-time-internal');
  const inputTimeType = document.getElementById('modal-time-type');
  const containerClientTime = document.getElementById('container-client-time-fields');
  const containerInternalTime = document.getElementById('container-internal-time-fields');

  if (btnTimeClient && btnTimeInternal) {
    btnTimeClient.onclick = () => {
      if (inputTimeType) inputTimeType.value = 'client';
      btnTimeClient.className = 'flex-1 py-1.5 text-xs font-semibold rounded bg-white text-text-primary shadow-sm transition-all text-center';
      btnTimeInternal.className = 'flex-1 py-1.5 text-xs font-semibold rounded text-text-secondary hover:text-text-primary transition-all text-center';
      containerClientTime?.classList.remove('hidden');
      containerInternalTime?.classList.add('hidden');
    };

    btnTimeInternal.onclick = () => {
      if (inputTimeType) inputTimeType.value = 'internal';
      btnTimeInternal.className = 'flex-1 py-1.5 text-xs font-semibold rounded bg-white text-text-primary shadow-sm transition-all text-center';
      btnTimeClient.className = 'flex-1 py-1.5 text-xs font-semibold rounded text-text-secondary hover:text-text-primary transition-all text-center';
      containerClientTime?.classList.add('hidden');
      containerInternalTime?.classList.remove('hidden');
    };
  }

  // --- Form Submissions ---
  const formSubtask = document.getElementById('form-log-subtask');
  if (formSubtask) {
    formSubtask.onsubmit = async (e) => {
      e.preventDefault();
      const isInternal = inputTimeType?.value === 'internal';
      const date = document.getElementById('modal-input-date').value;
      const actualHours = parseFloat(document.getElementById('modal-input-hours').value);
      const description = document.getElementById('modal-input-desc').value;
      let taskId = null;

      if (isInternal) {
        const category = document.getElementById('modal-select-internal-category')?.value || 'admin';
        try {
          const internalTasks = await DataService.getTasks(11); // Proyecto 11: Gestión Alerce
          let found = null;
          if (category === 'admin') {
            found = internalTasks.find(t => (t.title || '').toLowerCase().includes('admin'));
          } else if (category === 'ventas') {
            found = internalTasks.find(t => (t.title || '').toLowerCase().includes('ventas') || (t.title || '').toLowerCase().includes('comercial'));
          } else {
            found = internalTasks.find(t => (t.title || '').toLowerCase().includes('desarrollo') || (t.title || '').toLowerCase().includes('alerce'));
          }
          taskId = found ? found.id : (internalTasks[0]?.id || 470);
        } catch {
          taskId = 470; // Fallback tarea administración
        }
      } else {
        taskId = document.getElementById('modal-select-task').value;
        if (!taskId) {
          showToast('Debes seleccionar una tarea para imputar las horas', 'error');
          return;
        }
      }

      try {
        let hourlyRateApplied = 0;
        if (!isInternal) {
          const projId = Number(modalSelectProject?.value || selectedProjectId);
          const proj = cachedProjects.find(p => p.id === projId);
          if (proj && proj.client_id !== 12) {
            const baseRate = Number(proj.rate?.hourly_rate || 0);
            const disc = Number(proj.discount_percent || 0);
            hourlyRateApplied = Math.round(disc > 0 ? baseRate * (1 - disc / 100) : baseRate);
          }
        }

        await DataService.createSubtask({
          task_id: taskId,
          date,
          actual_hours: actualHours,
          author: 'Martín',
          hourly_rate_applied: hourlyRateApplied,
          description: isInternal ? `[Interna] ${description}` : description
        });
        if (subtaskLoggedFromTimer) {
          TimerService.reset();
          subtaskLoggedFromTimer = false;
          showToast(`Imputadas ${actualHours} hrs correctamente y cronómetro reiniciado`);
        } else {
          showToast(`Imputadas ${actualHours} hrs correctamente (${isInternal ? 'Hora interna' : 'Cliente'})`);
        }
        closeSubtaskModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al guardar: ${err.message}`, 'error');
      }
    };
  }

  const formProject = document.getElementById('form-new-project');
  if (formProject) {
    formProject.onsubmit = async (e) => {
      e.preventDefault();
      try {
        const newProj = await DataService.createProject({
          name: document.getElementById('modal-proj-name').value,
          client_id: document.getElementById('modal-proj-client').value || null,
          billing_mode: document.getElementById('modal-proj-billing').value,
          revenue_type: document.getElementById('modal-proj-revenue-type')?.value || 'puntual',
          rate_id: document.getElementById('modal-proj-rate').value || null,
          budget_hours: parseFloat(document.getElementById('modal-proj-hours').value) || 0,
          discount_percent: parseFloat(document.getElementById('modal-proj-discount')?.value) || 0,
          budget_amount: parseFloat(document.getElementById('modal-proj-amount').value) || 0,
          description: document.getElementById('modal-proj-desc').value,
          status: 'En curso'
        });
        showToast('Proyecto creado exitosamente');
        selectedProjectId = newProj.id;
        closeProjectModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      }
    };
  }

  // Auto-calculation helper: amount = (hours * rate) * (1 - discount/100)
  function setupProjectFormAutoCalculation(prefix) {
    const hoursInput = document.getElementById(`${prefix}-hours`);
    const rateSelect = document.getElementById(`${prefix}-rate`);
    const discountInput = document.getElementById(`${prefix}-discount`);
    const amountInput = document.getElementById(`${prefix}-amount`);

    if (!hoursInput || !rateSelect || !amountInput) return;

    const recalculate = () => {
      const hours = parseFloat(hoursInput.value) || 0;
      const rateId = rateSelect.value;
      const selectedRate = cachedRates.find(r => String(r.id) === String(rateId));
      const ratePerHour = Number(selectedRate?.hourly_rate || 0);
      const discount = parseFloat(discountInput?.value) || 0;

      if (hours > 0 && ratePerHour > 0) {
        const baseAmount = hours * ratePerHour;
        const finalAmount = discount > 0 ? baseAmount * (1 - (discount / 100)) : baseAmount;
        amountInput.value = Math.round(finalAmount * 100) / 100;
      }
    };

    hoursInput.addEventListener('input', recalculate);
    rateSelect.addEventListener('change', recalculate);
    if (discountInput) {
      discountInput.addEventListener('input', recalculate);
    }
  }

  setupProjectFormAutoCalculation('modal-proj');
  setupProjectFormAutoCalculation('edit-proj');

  const formTask = document.getElementById('form-add-task');
  if (formTask) {
    formTask.onsubmit = async (e) => {
      e.preventDefault();
      try {
        await DataService.createTask({
          project_id: selectedProjectId,
          title: document.getElementById('modal-task-title').value,
          priority: document.getElementById('modal-task-priority').value,
          estimated_hours: parseFloat(document.getElementById('modal-task-hours').value) || 0,
          start_date: document.getElementById('modal-task-start-date')?.value || null,
          due_date: document.getElementById('modal-task-due-date')?.value || null,
          status: 'Pendiente',
          owner: 'martin.batarev@gmail.com'
        });
        showToast('Tarea añadida al WBS');
        closeTaskModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      }
    };
  }

  // --- EDIT PROJECT MODAL & HANDLERS ---
  const btnEditProject = document.getElementById('btn-edit-project');
  const btnCloseEditProj = document.getElementById('btn-close-edit-project-modal');
  const btnCancelEditProj = document.getElementById('btn-cancel-edit-project');
  const formEditProject = document.getElementById('form-edit-project');

  if (btnEditProject) {
    btnEditProject.onclick = () => {
      const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      if (!proj || !editProjectModal) return;

      document.getElementById('edit-proj-id').value = proj.id;
      document.getElementById('edit-proj-name').value = proj.name || '';
      document.getElementById('edit-proj-client').value = proj.client_id || '';
      document.getElementById('edit-proj-status').value = proj.status || 'En curso';
      document.getElementById('edit-proj-billing').value = proj.billing_mode || 'fixed_budget';
      if (document.getElementById('edit-proj-revenue-type')) {
        document.getElementById('edit-proj-revenue-type').value = proj.revenue_type || 'puntual';
      }
      document.getElementById('edit-proj-rate').value = proj.rate_id || '';
      document.getElementById('edit-proj-hours').value = proj.budget_hours || 0;
      const editDiscount = document.getElementById('edit-proj-discount');
      if (editDiscount) editDiscount.value = proj.discount_percent || 0;
      document.getElementById('edit-proj-amount').value = proj.budget_amount || 0;
      document.getElementById('edit-proj-desc').value = proj.description || '';

      editProjectModal.classList.remove('hidden');
      document.body.classList.add('overflow-hidden');
    };
  }

  [btnCloseEditProj, btnCancelEditProj].forEach(b => {
    if (b) b.onclick = () => closeEditProjectModal();
  });

  if (formEditProject) {
    formEditProject.onsubmit = async (e) => {
      e.preventDefault();
      const projId = document.getElementById('edit-proj-id').value;
      try {
        await DataService.updateProject(projId, {
          name: document.getElementById('edit-proj-name').value,
          client_id: document.getElementById('edit-proj-client').value || null,
          status: document.getElementById('edit-proj-status').value,
          billing_mode: document.getElementById('edit-proj-billing').value,
          revenue_type: document.getElementById('edit-proj-revenue-type')?.value || 'puntual',
          rate_id: document.getElementById('edit-proj-rate').value || null,
          budget_hours: parseFloat(document.getElementById('edit-proj-hours').value) || 0,
          discount_percent: parseFloat(document.getElementById('edit-proj-discount')?.value) || 0,
          budget_amount: parseFloat(document.getElementById('edit-proj-amount').value) || 0,
          description: document.getElementById('edit-proj-desc').value
        });
        showToast('Proyecto actualizado exitosamente');
        closeEditProjectModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al actualizar proyecto: ${err.message}`, 'error');
      }
    };
  }

  // --- EDIT TASK MODAL & HANDLERS ---
  const btnCloseEditTask = document.getElementById('btn-close-edit-task-modal');
  const btnCancelEditTask = document.getElementById('btn-cancel-edit-task');
  const formEditTask = document.getElementById('form-edit-task');

  [btnCloseEditTask, btnCancelEditTask].forEach(b => {
    if (b) b.onclick = () => closeEditTaskModal();
  });

  if (formEditTask) {
    formEditTask.onsubmit = async (e) => {
      e.preventDefault();
      const taskId = document.getElementById('edit-task-id').value;
      try {
        await DataService.updateTask(taskId, {
          title: document.getElementById('edit-task-title').value,
          priority: document.getElementById('edit-task-priority').value,
          status: document.getElementById('edit-task-status').value,
          estimated_hours: parseFloat(document.getElementById('edit-task-hours').value) || 0,
          start_date: document.getElementById('edit-task-start-date')?.value || null,
          due_date: document.getElementById('edit-task-due-date')?.value || null
        });
        showToast('Tarea actualizada exitosamente');
        closeEditTaskModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al actualizar tarea: ${err.message}`, 'error');
      }
    };
  }

  // --- EDIT SUBTASK MODAL & HANDLERS ---
  const btnCloseEditSubtask = document.getElementById('btn-close-edit-subtask-modal');
  const btnCancelEditSubtask = document.getElementById('btn-cancel-edit-subtask');
  const formEditSubtask = document.getElementById('form-edit-subtask');

  [btnCloseEditSubtask, btnCancelEditSubtask].forEach(b => {
    if (b) b.onclick = () => closeEditSubtaskModal();
  });

  if (formEditSubtask) {
    formEditSubtask.onsubmit = async (e) => {
      e.preventDefault();
      const subtaskId = document.getElementById('edit-subtask-id').value;
      try {
        await DataService.updateSubtask(subtaskId, {
          date: document.getElementById('edit-subtask-date').value,
          actual_hours: parseFloat(document.getElementById('edit-subtask-hours').value) || 0,
          description: document.getElementById('edit-subtask-desc').value
        });
        showToast('Subtarea actualizada exitosamente');
        closeEditSubtaskModal();
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      } catch (err) {
        showToast(`Error al actualizar subtarea: ${err.message}`, 'error');
      }
    };
  }

  // --- MODAL DE AYUDA: PRESUPUESTO FIJO VS HORAS REALES ---
  const billingHelpModal = document.getElementById('modal-billing-help');
  const btnOpenBillingHelp = document.getElementById('btn-open-billing-help-modal');
  const btnCloseBillingHelp = document.getElementById('btn-close-billing-help-modal');
  const btnCloseBillingHelpFooter = document.getElementById('btn-close-billing-help-footer');

  const openBillingHelp = () => {
    if (!billingHelpModal) return;
    billingHelpModal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  };

  const closeBillingHelp = () => {
    if (!billingHelpModal) return;
    billingHelpModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  if (btnOpenBillingHelp) btnOpenBillingHelp.onclick = openBillingHelp;
  if (btnCloseBillingHelp) btnCloseBillingHelp.onclick = closeBillingHelp;
  if (btnCloseBillingHelpFooter) btnCloseBillingHelpFooter.onclick = closeBillingHelp;

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-open-billing-help');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      openBillingHelp();
    }
  });

  // --- MODAL IMPRESIÓN Y EXPORTACIÓN PDF A4 ---
  const printModal = document.getElementById('modal-print-preview');
  const btnOpenPrint = document.getElementById('btn-open-print-modal');
  const btnClosePrint = document.getElementById('btn-close-print-modal');
  const btnClosePrintFooter = document.getElementById('btn-close-print-footer');
  const btnTriggerPrint = document.getElementById('btn-trigger-print');
  const btnDownloadPdf = document.getElementById('btn-download-pdf');

  let printSelectedSubtaskIds = new Set();
  let printDateFrom = '';
  let printDateTo = '';
  let subtasksDrawerOpen = false;
  let currentDocInstance = null;

  const renderSubtaskChecklist = () => {
    const listContainer = document.getElementById('list-subtasks-checkboxes');
    if (!listContainer) return;

    const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
    if (!proj) return;

    const allSubtasks = (proj.tasks || []).flatMap(t => (t.subtasks || []).map(st => ({
      ...st,
      taskTitle: t.title
    }))).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

    // Filter list according to current date range
    const visibleSubtasks = allSubtasks.filter(st => {
      const stDate = st.date || '';
      if (printDateFrom && stDate < printDateFrom) return false;
      if (printDateTo && stDate > printDateTo) return false;
      return true;
    });

    if (visibleSubtasks.length === 0) {
      listContainer.innerHTML = '<p class="text-text-secondary text-center py-3 italic">No hay subtareas en el período seleccionado.</p>';
      return;
    }

    listContainer.innerHTML = visibleSubtasks.map(st => {
      const isChecked = printSelectedSubtaskIds.has(st.id);
      return `
        <div class="flex items-center justify-between p-2 rounded hover:bg-surface border border-transparent hover:border-border transition-colors">
          <label class="flex items-start gap-2.5 cursor-pointer flex-1 select-none">
            <input 
              type="checkbox" 
              class="chk-subtask-item mt-0.5 rounded border-border text-primary focus:ring-primary" 
              data-subtask-id="${st.id}" 
              ${isChecked ? 'checked' : ''} 
            />
            <div class="space-y-0.5">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-mono font-bold text-[11px] text-text-primary">${st.date || 'S/D'}</span>
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-surface border border-border text-text-secondary">${st.taskTitle}</span>
                <span class="font-mono text-emerald-700 font-bold text-[11px]">${formatHours(st.actual_hours)}</span>
              </div>
              <p class="text-[11px] text-text-secondary line-clamp-1">${st.description || 'Sin detalle técnico'}</p>
            </div>
          </label>
        </div>
      `;
    }).join('');

    // Wire individual checkboxes
    listContainer.querySelectorAll('.chk-subtask-item').forEach(chk => {
      chk.onchange = (e) => {
        const id = Number(e.target.dataset.subtaskId);
        if (e.target.checked) {
          printSelectedSubtaskIds.add(id);
        } else {
          printSelectedSubtaskIds.delete(id);
        }
        updateSelectAllState(visibleSubtasks);
        renderA4Document();
      };
    });

    updateSelectAllState(visibleSubtasks);
  };

  const updateSelectAllState = (visibleSubtasks) => {
    const chkSelectAll = document.getElementById('chk-select-all-subtasks');
    if (!chkSelectAll || !visibleSubtasks) return;
    const allChecked = visibleSubtasks.length > 0 && visibleSubtasks.every(st => printSelectedSubtaskIds.has(st.id));
    chkSelectAll.checked = allChecked;
  };

  const applyPeriodFilter = (from, to) => {
    printDateFrom = from;
    printDateTo = to;
    const fromEl = document.getElementById('input-print-from');
    const toEl = document.getElementById('input-print-to');
    if (fromEl) fromEl.value = from;
    if (toEl) toEl.value = to;

    const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
    if (!proj) return;

    const allSubtasks = (proj.tasks || []).flatMap(t => t.subtasks || []);
    printSelectedSubtaskIds.clear();

    allSubtasks.forEach(st => {
      const stDate = st.date || '';
      let matches = true;
      if (from && stDate < from) matches = false;
      if (to && stDate > to) matches = false;
      if (matches) {
        printSelectedSubtaskIds.add(st.id);
      }
    });

    renderSubtaskChecklist();
    renderA4Document();
  };

  let cachedCompanyProfile = null;

  const renderA4Document = async () => {
    const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
    if (!proj) return;

    if (!cachedCompanyProfile) {
      cachedCompanyProfile = await DataService.getCompanyProfile();
    }

    const today = new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' });
    const inputDateEl = document.getElementById('input-print-date');
    const docDate = inputDateEl?.value 
      ? new Date(inputDateEl.value + 'T00:00:00').toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })
      : today;

    const isTimeAndMaterials = proj.billing_mode === 'time_and_materials';

    let options = { docDate, companyProfile: cachedCompanyProfile };

    if (isTimeAndMaterials) {
      const showSummary = document.getElementById('chk-print-summary')?.checked ?? true;
      const showTasks = document.getElementById('chk-print-tasks')?.checked ?? true;
      const showSubtasks = document.getElementById('chk-print-subtasks')?.checked ?? true;

      options = {
        ...options,
        selectedSubtaskIds: printSelectedSubtaskIds,
        dateFrom: printDateFrom,
        dateTo: printDateTo,
        showSummary,
        showTasks,
        showSubtasks
      };

      // Recalcular métricas para el badge de selección en la barra de configuración
      const allSubtasks = (proj.tasks || []).flatMap(t => t.subtasks || []);
      const includedSubtasks = allSubtasks.filter(st => printSelectedSubtaskIds.has(st.id));
      const totalHours = includedSubtasks.reduce((sum, st) => sum + Number(st.actual_hours || 0), 0);
      const hourlyRate = Number(proj.rate?.hourly_rate || 0);
      const rawTmAmount = totalHours * hourlyRate;
      const discountPercent = Number(proj.discount_percent || 0);
      const billableAmount = discountPercent > 0 ? rawTmAmount * (1 - discountPercent / 100) : rawTmAmount;

      const summaryBadge = document.getElementById('print-selection-summary');
      if (summaryBadge) {
        summaryBadge.textContent = `${includedSubtasks.length} de ${allSubtasks.length} subtareas · ${formatHours(totalHours)} · ${formatMoney(billableAmount, proj.rate?.currency)}`;
      }
    } else {
      const validitySelect = document.getElementById('select-print-validity');
      const validityDays = Number(validitySelect?.value || proj.validity_days || 15);
      options = {
        ...options,
        validityDays
      };
    }

    try {
      // 1. Renderizar hojas A4 reales en el contenedor visual
      const sheetsContainer = document.getElementById('printable-sheets-container');
      if (sheetsContainer) {
        const pages = PdfService.buildDocumentHtmlPages(proj, options);
        sheetsContainer.innerHTML = pages.map((pageHtml, idx) => {
          if (idx === 0) return pageHtml;
          return `
            <div class="w-full max-w-[794px] py-2 mb-6 flex items-center justify-center gap-3 text-zinc-500 text-xs font-mono select-none no-print">
              <span class="h-px bg-zinc-300 flex-1"></span>
              <span class="bg-zinc-200 px-3 py-1 rounded-full text-[11px] font-bold text-zinc-700">Hoja ${idx + 1} de ${pages.length}</span>
              <span class="h-px bg-zinc-300 flex-1"></span>
            </div>
            ${pageHtml}
          `;
        }).join('');

        const pageBadge = document.getElementById('pdf-page-count-badge');
        if (pageBadge) {
          pageBadge.textContent = `${pages.length} ${pages.length === 1 ? 'Página A4' : 'Páginas A4'}`;
        }
      }

      // 2. Generar instancia de jsPDF para descarga directa
      currentDocInstance = PdfService.generateProjectDocument(proj, options);
    } catch (err) {
      console.error('Error generando documento A4:', err);
      showToast('Error al generar la previsualización del documento', 'error');
    }
  };

  const openPrintModal = async () => {
    if (!printModal) return;

    cachedCompanyProfile = await DataService.getCompanyProfile();
    const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
    if (proj && proj.billing_mode === 'time_and_materials') {
      const allSubtasks = (proj.tasks || []).flatMap(t => t.subtasks || []);
      printSelectedSubtaskIds = new Set(allSubtasks.map(st => st.id));
      printDateFrom = '';
      printDateTo = '';
      subtasksDrawerOpen = false;

      const fromEl = document.getElementById('input-print-from');
      const toEl = document.getElementById('input-print-to');
      if (fromEl) fromEl.value = '';
      if (toEl) toEl.value = '';

      const drawer = document.getElementById('drawer-subtasks-selector');
      if (drawer) drawer.classList.add('hidden');

      renderSubtaskChecklist();
    }

    printModal.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
    await renderA4Document();
  };

  const closePrintModal = () => {
    if (!printModal) return;
    printModal.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('is-printing-a4-doc');
  };

  if (btnOpenPrint) btnOpenPrint.onclick = openPrintModal;
  if (btnClosePrint) btnClosePrint.onclick = closePrintModal;
  if (btnClosePrintFooter) btnClosePrintFooter.onclick = closePrintModal;

  if (btnDownloadPdf) {
    btnDownloadPdf.onclick = () => {
      const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      if (!proj || !currentDocInstance) return;
      const safeProjName = (proj.name || 'Equipo_Alerce').replace(/[^a-zA-Z0-9_-]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = proj.billing_mode === 'time_and_materials'
        ? `Remito_${safeProjName}_${dateStr}.pdf`
        : `Presupuesto_${safeProjName}_${dateStr}.pdf`;

      currentDocInstance.save(fileName);
      showToast(`Documento "${fileName}" descargado con éxito`, 'success');
    };
  }

  if (btnTriggerPrint) {
    btnTriggerPrint.onclick = () => {
      document.body.classList.add('is-printing-a4-doc');
      window.print();
    };
  }

  window.addEventListener('afterprint', () => {
    document.body.classList.remove('is-printing-a4-doc');
  });

  // Quick period buttons
  document.querySelectorAll('.btn-period-quick').forEach(btn => {
    btn.onclick = () => {
      const period = btn.dataset.period;
      const now = new Date();
      const Y = now.getFullYear();
      const M = now.getMonth();

      if (period === 'all') {
        applyPeriodFilter('', '');
      } else if (period === 'this-month') {
        const from = `${Y}-${String(M + 1).padStart(2, '0')}-01`;
        const to = `${Y}-${String(M + 1).padStart(2, '0')}-${String(new Date(Y, M + 1, 0).getDate()).padStart(2, '0')}`;
        applyPeriodFilter(from, to);
      } else if (period === 'last-month') {
        const prevDate = new Date(Y, M - 1, 1);
        const pY = prevDate.getFullYear();
        const pM = prevDate.getMonth();
        const from = `${pY}-${String(pM + 1).padStart(2, '0')}-01`;
        const to = `${pY}-${String(pM + 1).padStart(2, '0')}-${String(new Date(pY, pM + 1, 0).getDate()).padStart(2, '0')}`;
        applyPeriodFilter(from, to);
      } else if (period === 'last-15') {
        const past = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
        const from = past.toISOString().split('T')[0];
        const to = now.toISOString().split('T')[0];
        applyPeriodFilter(from, to);
      }
    };
  });

  // Date range inputs change
  const inputPrintFrom = document.getElementById('input-print-from');
  const inputPrintTo = document.getElementById('input-print-to');

  if (inputPrintFrom) {
    inputPrintFrom.onchange = (e) => {
      applyPeriodFilter(e.target.value, inputPrintTo?.value || '');
    };
  }

  if (inputPrintTo) {
    inputPrintTo.onchange = (e) => {
      applyPeriodFilter(inputPrintFrom?.value || '', e.target.value);
    };
  }

  // Toggle Subtasks Selector Drawer
  const btnToggleDrawer = document.getElementById('btn-toggle-subtasks-drawer');
  const drawer = document.getElementById('drawer-subtasks-selector');
  if (btnToggleDrawer && drawer) {
    btnToggleDrawer.onclick = () => {
      subtasksDrawerOpen = !subtasksDrawerOpen;
      if (subtasksDrawerOpen) {
        drawer.classList.remove('hidden');
        btnToggleDrawer.classList.add('bg-surface-subtle', 'ring-1', 'ring-primary');
      } else {
        drawer.classList.add('hidden');
        btnToggleDrawer.classList.remove('bg-surface-subtle', 'ring-1', 'ring-primary');
      }
    };
  }

  // Select All / Deselect All subtasks checkbox
  const chkSelectAll = document.getElementById('chk-select-all-subtasks');
  if (chkSelectAll) {
    chkSelectAll.onchange = (e) => {
      const checked = e.target.checked;
      const proj = cachedProjects.find(p => p.id === Number(selectedProjectId));
      if (!proj) return;
      const allSubtasks = (proj.tasks || []).flatMap(t => t.subtasks || []);

      allSubtasks.forEach(st => {
        const stDate = st.date || '';
        let inRange = true;
        if (printDateFrom && stDate < printDateFrom) inRange = false;
        if (printDateTo && stDate > printDateTo) inRange = false;
        if (inRange) {
          if (checked) {
            printSelectedSubtaskIds.add(st.id);
          } else {
            printSelectedSubtaskIds.delete(st.id);
          }
        }
      });

      renderSubtaskChecklist();
      renderA4Document();
    };
  }

  // Interactive print configuration controls
  ['chk-print-summary', 'chk-print-tasks', 'chk-print-subtasks', 'select-print-validity', 'input-print-date'].forEach(id => {
    const el = document.getElementById(id);
    if (el) {
      el.onchange = renderA4Document;
      if (el.tagName === 'INPUT' && el.type === 'date') {
        el.oninput = renderA4Document;
      }
    }
  });

  // Unified Backdrop Click & Escape Key Handler for all modals
  const allModalHandlers = [
    { modal: subtaskModal, close: closeSubtaskModal },
    { modal: projectModal, close: closeProjectModal },
    { modal: taskModal, close: closeTaskModal },
    { modal: editProjectModal, close: closeEditProjectModal },
    { modal: editTaskModal, close: closeEditTaskModal },
    { modal: editSubtaskModal, close: closeEditSubtaskModal },
    { modal: billingHelpModal, close: closeBillingHelp },
    { modal: printModal, close: closePrintModal }
  ];

  allModalHandlers.forEach(({ modal, close }) => {
    if (modal) {
      modal.onclick = (e) => {
        if (e.target === modal) close();
      };
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      allModalHandlers.forEach(({ modal, close }) => {
        if (modal && !modal.classList.contains('hidden')) {
          close();
        }
      });
    }
  });
}



